import React, { createContext, Dispatch, PropsWithChildren, SetStateAction, useCallback, useContext, useReducer, useState, useEffect, useRef, useMemo } from "react";
import { AppContext } from "Context/AppContext";
import { initialMappingState, initialFilterState, mappingReducer, avRowKey } from "./mappingReducer";
import {
    updateMappingData,
    getAttributeMappings,
    getAttributeValueMappings,
    getAttributeValueUniqueCount,
    getAttributeValueFilteredAttributes,
    getCategoryMappings,
    getCategoryDropdownOptions,
    bulkReviewAttributeValues,
    pollBulkReviewJob
} from "../Services/MappingServices";
import Utils from "Common/Utils";
import { toast } from "Components/DesignSystem/Toast/useToast";
import { hasMeaningfulChanges } from "../Utils/categoryValidation";
import { pickAttributeToSelect } from "../Utils/selectedAttribute";
import UnsavedChangesModal from "Components/Common/UnsavedChangesModal";
import { PAGE_SIZE } from "../Constants/Constants";

export type MappingContextType = {
    industryId: string | null | undefined;
    channelKey: string;
    activeTab: MappingTabType;
    onTabChange: (tab: MappingTabType) => void;
    readPermission: boolean;
    updatePermission: boolean;
    bulkUpdatePermission: boolean;
    loading: boolean;
    isLoadingAttributeValues: boolean;
    isEditMode: boolean;
    tableSearchValue: string;
    onSearchTableData: (value: string) => void;
    isFilterOpen: boolean;
    setIsFilterOpen: Dispatch<SetStateAction<boolean>>;
    handleEditClick: () => void;
    isSaving: boolean;
    setIsSaving: Dispatch<SetStateAction<boolean>>;

    mappingState: MappingState;
    updateAttribute: (id: string, changes: Partial<MappingAttribute>) => void;
    updateAttributeValue: (id: string, changes: Partial<MappingAttributeValue>) => void;
    updateCategory: (id: string, changes: Partial<MappingCategory>) => void;
    addAttributePairToCategory: (categoryId: string) => void;
    removeAttributePair: (categoryId: string, pairIndex: number) => void;
    updateAttributePair: (categoryId: string, pairIndex: number, changes: Partial<AttributeValuePair>) => void;
    getCurrentTabData: () => MappingAttribute[] | MappingAttributeValue[] | MappingCategory[];
    getCategoryDropdownAttributes: (page?: number, search?: string) => Promise<{ items: { value: string; label: string }[]; hasMore: boolean }>;
    loadMoreCategoryDropdownAttributes: () => Promise<{ items: { value: string; label: string }[]; hasMore: boolean }>;
    getCategoryDropdownAttributeValues: (attributeName: string, page?: number, search?: string) => Promise<{ items: { value: string; label: string }[]; hasMore: boolean }>;
    loadMoreCategoryDropdownAttributeValues: (attributeName: string) => Promise<{ items: { value: string; label: string }[]; hasMore: boolean }>;
    clearCategoryDropdownCache: () => void;
    saveChanges: () => Promise<boolean>;
    discardChanges: () => void;
    loadInitialData: () => Promise<void>;

    // Pagination
    pagination: MappingState['pagination'];
    loadMoreData: () => void;
    loadMoreAttributes: () => void;

    // Filter properties
    filterState: FilterState;
    updateFilter: (filterType: string, value: any) => void;
    toggleFilterValue: (filterType: string, value: string) => void;
    clearFilter: (filterId: string) => void;
    clearAllFilters: () => void;
    getActiveFilterChips: (tabType?: MappingTabType) => FilterChip[];
    // Row actions for bulk operations
    attributeRowActions: RowActionsData | null;
    attributeValueRowActions: RowActionsData | null;
    categoryRowActions: RowActionsData | null;
    setAttributeRowActions: (actions: RowActionsData | null) => void;
    setAttributeValueRowActions: (actions: RowActionsData | null) => void;
    setCategoryRowActions: (actions: RowActionsData | null) => void;

    copyCategory: (category: MappingCategory) => string;
    deleteCategory: (categoryId: string) => void;

    selectedAttribute: MappingAttribute | null;
    setSelectedAttribute: (attribute: MappingAttribute | null) => void;
    getNoDataMessage: () => { title: string; message: string; }
    setCategoryTableVisibleData: React.Dispatch<React.SetStateAction<MappingCategory[]>>;
    getAllCategories: () => MappingCategory[];
    bulkReviewAllAttributeValues: () => Promise<{ done: boolean; message: string; updated_count?: number }>;

    // Sidebar attribute search (server-side)
    searchAttributesList: (searchTerm: string) => Promise<void>;

    // Incremented when consumers should reset their local search inputs (e.g. after save)
    listSearchResetSignal: number;

    // Sort/filter state and handlers
    sortState: {
        attributes: { sortBy: string; sortOrder: 'asc' | 'desc' };
        attributeValues: { sortBy: string; sortOrder: 'asc' | 'desc' };
        categories: { sortBy: string; sortOrder: 'asc' | 'desc' };
    };
    handleSortChange: (column: string) => void;
};

export const MappingContext = createContext<MappingContextType>({} as MappingContextType);

export const MappingContextProvider = ({ children }: PropsWithChildren) => {
    const appContext = useContext(AppContext);
    const industryId: string | null | undefined = appContext.get_selected_industry() && appContext?.get_selected_industry()?.id;
    const channelKey: string = appContext?.channelKey;
    const [activeTab, setActiveTab] = useState<MappingTabType>('attribute');
    const [tableSearchValue, setTableSearchValue] = useState<string>('');
    // Debounced mirror of tableSearchValue, consumed ONLY by the attributevalue
    // tab's value fetch (the selectedAttribute effect). The attribute/category
    // tabs debounce their search via searchDebounceRef → loadInitialData; the
    // attributevalue values fetch is instead driven by the selectedAttribute
    // effect, which — keyed directly on tableSearchValue — fired one request
    // per keystroke (c, co, col, colo, color). Empty flushes immediately so
    // clearing the search or switching attribute reloads unfiltered with no lag.
    const [debouncedTableSearchValue, setDebouncedTableSearchValue] = useState<string>('');
    // The setter is intentionally not destructured: the signal is wired
    // through context so AttributeSelectorList CAN react to a reset on
    // demand, but no current save / discard path actually fires it (we
    // preserve the sidebar search across saves instead — see saveChanges).
    // Keeping the value plumbed-but-quiescent so a future flow can opt
    // into resetting the local search by bumping it via context.
    const [listSearchResetSignal] = useState(0);
    // Sidebar attribute-search term. Lifted into context (in addition to the
    // local AttributeSelectorList state) so context-level refetches — namely
    // reloadAttributesListForAttributeValueTab on filter change — can apply
    // the current sidebar search instead of dropping it.
    const attributeListSearchTermRef = useRef('');
    const [isEditMode, setIsEditMode] = useState(false);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingAttributeValues, setIsLoadingAttributeValues] = useState(false);

    // Row actions state for each tab
    const [attributeRowActions, setAttributeRowActions] = useState<RowActionsData | null>(null);
    const [attributeValueRowActions, setAttributeValueRowActions] = useState<RowActionsData | null>(null);
    const [categoryRowActions, setCategoryRowActions] = useState<RowActionsData | null>(null);

    const [mappingState, dispatchMapping] = useReducer(mappingReducer, initialMappingState);
    const [filterState, setFilterState] = useState<FilterState>(initialFilterState);
    const [selectedAttribute, setSelectedAttribute] = useState<MappingAttribute | null>(null);

    const [categoryTableVisibleData, setCategoryTableVisibleData] = useState<MappingCategory[]>([]);

    // Sort and filter state for each tab
    const [sortState, setSortState] = useState<{
        attributes: { sortBy: string; sortOrder: 'asc' | 'desc' };
        attributeValues: { sortBy: string; sortOrder: 'asc' | 'desc' };
        categories: { sortBy: string; sortOrder: 'asc' | 'desc' };
    }>({
        attributes: { sortBy: 'attribute', sortOrder: 'asc' },
        attributeValues: { sortBy: 'attribute_value', sortOrder: 'asc' },
        categories: { sortBy: 'reporting_category', sortOrder: 'asc' }
    });

    // Reset editInProgress and clear handlers on unmount
    useEffect(() => {
        return () => {
            appContext?.setEditInProgress(false);
            appContext?.setEditSaveHandler(null);
            appContext?.setEditCancelHandler(null);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Debounce timer for search
    const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);

    // Track if initial mount has completed to prevent double load
    const initialMountRef = useRef<boolean>(true);

    // Guard to prevent concurrent loadInitialData calls
    const isLoadingInitialDataRef = useRef<boolean>(false);
    // A call that arrives while a load is in flight is queued, not dropped: the
    // dropped call is usually the one carrying the user's LATEST filter (e.g.
    // Reviewed -> Pending in two quick clicks), and dropping it left page 1
    // loaded with the old filter while scroll pages used the new one (AB#15926).
    const pendingInitialLoadRef = useRef<{ tabOverride?: MappingTabType } | null>(null);
    const loadInitialDataRef = useRef<(tabOverride?: MappingTabType) => Promise<void>>(async () => {});

    // Track if we've completed the first data load (to prevent search effect from triggering on mount)
    const hasCompletedInitialLoadRef = useRef<boolean>(false);

    // Ref to hold current mapping state for stable access in callbacks
    const mappingStateRef = useRef(mappingState);

    // Refs to hold current search and filter values for stable access in callbacks
    const tableSearchValueRef = useRef(tableSearchValue);
    const filterStateRef = useRef(filterState);
    // Tracks the currently selected attribute so async reloads can preserve it
    // across save/sort refetches without stale closures.
    const selectedAttributeRef = useRef<MappingAttribute | null>(null);

    // Cache for category dropdown options with pagination support (to avoid repeated API calls)
    const [categoryDropdownCache, setCategoryDropdownCache] = useState<{
        attributes: {
            items: { value: string; label: string }[];
            hasMore: boolean;
            currentPage: number;
        } | null;
        attributeValuesByAttribute: Record<string, {
            items: { value: string; label: string }[];
            hasMore: boolean;
            currentPage: number;
        }>;
    }>({
        attributes: null,
        attributeValuesByAttribute: {}
    });

    // Refs to track loading promises and prevent race conditions
    const attributesLoadingPromiseRef = useRef<Promise<{ items: { value: string; label: string }[]; hasMore: boolean }> | null>(null);
    const attributeValuesLoadingPromisesRef = useRef<Record<string, Promise<{ items: { value: string; label: string }[]; hasMore: boolean }>>>({});

    const readPermission = appContext.pm.checkPermission("gmsdatamapping_read");
    const updatePermission = appContext.pm.checkPermission("gmsdatamapping_update");
    const bulkUpdatePermission = appContext.pm.checkPermission("gmsdatamapping_bulkupdate");

    // Tracking which tabs have been loaded
    const [loadedTabs, setLoadedTabs] = useState<Set<MappingTabType>>(new Set());

    // Build filter parameters from filterState
    // Memoized to prevent unnecessary recalculations
    const buildFilterParams = useCallback((tab?: MappingTabType): any => {
        const targetTab = tab || activeTab;
        const params: any = {};

        // Add search parameter
        if (tableSearchValue && tableSearchValue.trim() !== '') {
            params.search = tableSearchValue.trim();
        }

        // Add consider filter
        if (filterState.consider.length === 1) {
            params.is_considered = filterState.consider[0] === 'on';
        }

        // Add review filter
        if (filterState.review.length === 1) {
            params.is_reviewed = filterState.review[0] === 'reviewed';
        }

        // Add tab-specific filters
        if (targetTab === 'attribute') {
            if (filterState.attributes.incoming.length > 0) {
                params.attribute = filterState.attributes.incoming;
            }
            if (filterState.attributes.mapped.length > 0) {
                params.mapped_attribute = filterState.attributes.mapped;
            }
        } else if (targetTab === 'attributevalue') {
            if (filterState.attributeValues.incoming.length > 0) {
                params.attribute_value = filterState.attributeValues.incoming;
            }
            if (filterState.attributeValues.mapped.length > 0) {
                params.mapped_attribute_value = filterState.attributeValues.mapped;
            }
        } else if (targetTab === 'category') {
            if (filterState.categories.reportingCategory.length > 0) {
                params.reporting_category = filterState.categories.reportingCategory;
            }
            if (filterState.categories.mappedCategory.length > 0) {
                params.mapped_category = filterState.categories.mappedCategory;
            }
        }

        return params;
    }, [activeTab, tableSearchValue, filterState]);

    // The infinite-scroll loaders (loadMoreData / loadMoreAttributes) deliberately
    // leave buildFilterParams out of their deps, so calling it directly reads the
    // filter state captured when the loader was last memoised. That made page 2+
    // drop a just-applied Review/Consider filter and mix reviewed rows into a
    // "Review Pending" view (AB#15926). Read it through this ref instead.
    const buildFilterParamsRef = useRef(buildFilterParams);
    buildFilterParamsRef.current = buildFilterParams;

    // Params for the AV-tab attribute sidebar: Consider/Review only (never the
    // table search or attribute-tab filters). Page 1 (reload) and page 2+
    // (loadMoreAttributes) must send the same set or later pages diverge.
    const buildSidebarFilterParams = (): any => {
        const currentFilterState = filterStateRef.current;
        const filterParams: any = {};
        if (currentFilterState.consider.length === 1) {
            filterParams.is_considered = currentFilterState.consider[0] === 'on';
        }
        if (currentFilterState.review.length === 1) {
            filterParams.is_reviewed = currentFilterState.review[0] === 'reviewed';
        }
        return filterParams;
    };

    // Handle sort change - updates sort state and reloads data
    const handleSortChange = useCallback((column: string) => {
        setSortState(prev => {
            const currentTabSort = activeTab === 'attribute'
                ? prev.attributes
                : activeTab === 'attributevalue'
                    ? prev.attributeValues
                    : prev.categories;

            const isSameColumn = currentTabSort.sortBy === column;
            const newSortOrder = isSameColumn && currentTabSort.sortOrder === 'asc' ? 'desc' : 'asc';

            const newState = {
                ...prev,
                [activeTab === 'attribute' ? 'attributes' : activeTab === 'attributevalue' ? 'attributeValues' : 'categories']: {
                    sortBy: column,
                    sortOrder: newSortOrder
                }
            };

            return newState;
        });

        // Reset and reload data with new sort
        dispatchMapping({
            type: 'RESET_TAB_DATA',
            payload: { tab: activeTab }
        });
    }, [activeTab]);

    // Reload just the attributes list for attributevalue tab (without auto-selecting)
    // This is used when filters change to refresh the left-side attribute list
    const reloadAttributesListForAttributeValueTab = useCallback(async () => {
        if (!channelKey || activeTab !== 'attributevalue') return;

        try {
            const page_size = PAGE_SIZE;

            // NOTE: Do NOT include tableSearchValue here - the table search should only
            // affect attribute values, not the attribute selector list
            const filterParams = buildSidebarFilterParams();

            const params: AttributeRequestParams = {
                page: 1,
                page_size,
                sort_by: sortState.attributes.sortBy,
                sort_order: sortState.attributes.sortOrder,
                ...filterParams
            };

            // Carry the sidebar attribute-search term across filter changes so
            // the user's narrowed list stays narrowed when they then apply
            // Consider/Review filters.
            const sidebarSearch = attributeListSearchTermRef.current.trim();
            if (sidebarSearch) {
                params.search = sidebarSearch;
            }


            // Always use attributevalue-attributes on this tab — see the
            // matching comment in `loadInitialData` for why. Switching APIs
            // based on filter state caused the left list to oscillate between
            // incoming names (unfiltered) and mapped names (filtered).
            const res = await getAttributeValueFilteredAttributes(channelKey, params);

            if (res && res.done && res.data && res.data.items && res.data.pagination) {
                // Update attributes list
                dispatchMapping({
                    type: 'LOAD_DATA_SUCCESS_PAGINATED',
                    payload: {
                        tab: 'attribute',
                        data: res.data.items,
                        pagination: res.data.pagination
                    }
                });


                // Preserve the user's current selection across sort/filter reloads; only fall
                // back to auto-select when the previous selection is gone from the list.
                const sortedAttributes = [...res.data.items].sort((a: MappingAttribute, b: MappingAttribute) => {
                    const nameA = (a.attribute || a.mapped_attribute || '').toLowerCase();
                    const nameB = (b.attribute || b.mapped_attribute || '').toLowerCase();
                    return nameA.localeCompare(nameB);
                });

                setSelectedAttribute(pickAttributeToSelect(sortedAttributes, selectedAttributeRef.current));
            }
        } catch (error) {
            console.error('[reloadAttributesList] Failed to reload attributes list:', error);
        }
    }, [channelKey, activeTab, sortState.attributes]);

    // Load initial data based on active tab (lazy loading with pagination)
    // tabOverride allows explicit tab specification when called from onTabChange
    const loadInitialData = useCallback(async (tabOverride?: MappingTabType) => {
        const targetTab = tabOverride || activeTab;

        if (!channelKey) return;

        // Guard to prevent concurrent calls — queue a single re-run instead of
        // dropping this one (see pendingInitialLoadRef).
        if (isLoadingInitialDataRef.current) {
            pendingInitialLoadRef.current = { tabOverride };
            return;
        }

        isLoadingInitialDataRef.current = true;

        try {
            dispatchMapping({ type: 'SET_LOADING', payload: true });

            // Use consistent page size for infinite scroll across all tabs
            const page_size = PAGE_SIZE;

            // Build filter params inline using refs to always get current values
            // This prevents stale closure issues with useCallback
            const currentSearchValue = tableSearchValueRef.current;
            const currentFilterState = filterStateRef.current;

            const filterParams: any = {};
            if (currentSearchValue && currentSearchValue.trim() !== '') {
                filterParams.search = currentSearchValue.trim();
            }
            if (currentFilterState.consider.length === 1) {
                filterParams.is_considered = currentFilterState.consider[0] === 'on';
            }
            if (currentFilterState.review.length === 1) {
                filterParams.is_reviewed = currentFilterState.review[0] === 'reviewed';
            }
            // Add tab-specific filters
            if (targetTab === 'attribute') {
                if (currentFilterState.attributes.incoming.length > 0) {
                    filterParams.attribute = currentFilterState.attributes.incoming;
                }
                if (currentFilterState.attributes.mapped.length > 0) {
                    filterParams.mapped_attribute = currentFilterState.attributes.mapped;
                }
            } else if (targetTab === 'attributevalue') {
                if (currentFilterState.attributeValues.incoming.length > 0) {
                    filterParams.attribute_value = currentFilterState.attributeValues.incoming;
                }
                if (currentFilterState.attributeValues.mapped.length > 0) {
                    filterParams.mapped_attribute_value = currentFilterState.attributeValues.mapped;
                }
            } else if (targetTab === 'category') {
                if (currentFilterState.categories.reportingCategory.length > 0) {
                    filterParams.reporting_category = currentFilterState.categories.reportingCategory;
                }
                if (currentFilterState.categories.mappedCategory.length > 0) {
                    filterParams.mapped_category = currentFilterState.categories.mappedCategory;
                }
            }

            // Load based on active tab
            // Always load for attribute tab (removed currentPage check to fix empty tab issue)
            if (targetTab === 'attribute') {
                const params: AttributeRequestParams = {
                    page: 1,
                    page_size,
                    sort_by: sortState.attributes.sortBy,
                    sort_order: sortState.attributes.sortOrder,
                    ...filterParams
                };

                const res = await getAttributeMappings(channelKey, params);

                if (res && res.done) {
                    // Check if response is paginated
                    if (res.data && res.data.items && res.data.pagination) {
                        // Paginated response
                        dispatchMapping({
                            type: 'LOAD_DATA_SUCCESS_PAGINATED',
                            payload: {
                                tab: 'attribute',
                                data: res.data.items,
                                pagination: res.data.pagination
                            }
                        });

                        // Sort the attributes and preserve the user's current selection across refetches.
                        const sortedAttributes = [...res.data.items].sort((a: MappingAttribute, b: MappingAttribute) => {
                            const nameA = (a.attribute || a.mapped_attribute || '').toLowerCase();
                            const nameB = (b.attribute || b.mapped_attribute || '').toLowerCase();
                            return nameA.localeCompare(nameB);
                        });

                        const nextSelection = pickAttributeToSelect(sortedAttributes, selectedAttributeRef.current);
                        if (nextSelection) {
                            setSelectedAttribute(nextSelection);
                        }

                        // Only update if not already loaded to prevent infinite loops
                        setLoadedTabs(prev => {
                            if (prev.has('attribute')) return prev;
                            const newSet = new Set(prev);
                            newSet.add('attribute');
                            return newSet;
                        });
                    }
                }
            } else if (targetTab === 'attributevalue') {
                // For attributevalue tab, pre-load attributes using the appropriate API
                // Don't pass table search to the attribute list API on attributevalue tab
                // Table search should only affect attribute values, not the sidebar
                const { search: _excludeSearch, ...attributeFilterParams } = filterParams;

                const params: AttributeRequestParams = {
                    page: 1,
                    page_size,
                    sort_by: sortState.attributes.sortBy,
                    sort_order: sortState.attributes.sortOrder,
                    ...attributeFilterParams
                };

                // The sidebar attribute search is a separate state from the
                // in-table value search — clearing tableSearchValue above
                // (via the de-structure) is correct, but we still need to
                // honor the sidebar narrowing the user typed. Matches what
                // reloadAttributesListForAttributeValueTab does on filter
                // changes; without this, a Save (which calls back into
                // loadInitialData) blows away the user's "base color" search
                // and shows the full attribute list even though the search
                // input still displays the term.
                const sidebarSearch = attributeListSearchTermRef.current.trim();
                if (sidebarSearch) {
                    params.search = sidebarSearch;
                }


                // Always use attributevalue-attributes on this tab — it groups
                // AV docs by mapped_attribute (Phase 2 key) and returns each
                // mapped GMS attribute exactly once. The previous branch that
                // switched to /attributes when no filters were active surfaced
                // incoming names (e.g. "Video 1 Name", "Video 2 Name", ...)
                // instead of the deduplicated mapped name ("Video Name"),
                // which then mismatched against AV docs whose `mapped_attribute`
                // is the mapped name — silently producing an empty values
                // table and broken value counts.
                const res = await getAttributeValueFilteredAttributes(channelKey, params);

                if (res && res.done) {
                    // Check if response is paginated
                    if (res.data && res.data.items && res.data.pagination) {
                        // Paginated response
                        dispatchMapping({
                            type: 'LOAD_DATA_SUCCESS_PAGINATED',
                            payload: {
                                tab: 'attribute',  // Store in attributes array for selector list
                                data: res.data.items,
                                pagination: res.data.pagination
                            }
                        });

                        // Sort the attributes and preserve the user's current selection across refetches.
                        const sortedAttributes = [...res.data.items].sort((a: MappingAttribute, b: MappingAttribute) => {
                            const nameA = (a.attribute || a.mapped_attribute || '').toLowerCase();
                            const nameB = (b.attribute || b.mapped_attribute || '').toLowerCase();
                            return nameA.localeCompare(nameB);
                        });

                        const nextSelection = pickAttributeToSelect(sortedAttributes, selectedAttributeRef.current);
                        if (nextSelection) {
                            setSelectedAttribute(nextSelection);
                        }

                        setLoadedTabs(prev => {
                            const newSet = new Set(prev);
                            newSet.add('attributevalue');
                            return newSet;
                        });
                    }
                }
            } else if (targetTab === 'category') {
                // For category tab, we need to pre-load attributes if not already loaded
                // This ensures dropdown options are available for category mapping
                const currentAttributes = mappingStateRef.current.working.attributes;
                const hasAttributes = currentAttributes && currentAttributes.length > 0;

                if (!hasAttributes) {

                    // Use the new dedicated dropdown API - only loads attribute names (not values)
                    // This is lightweight and returns only considered & reviewed items
                    const dropdownRes = await getCategoryDropdownOptions(channelKey);

                    if (dropdownRes && dropdownRes.done && dropdownRes.data.attributes) {

                        // Transform to MappingAttribute format for dropdown generation
                        const attributesForDropdown = dropdownRes.data.attributes.map((attr: any) => ({
                            attribute: attr.attribute,
                            mapped_attribute: attr.mapped_attribute,
                            isConsidered: true,
                            isReviewed: true,
                            attributeValues: [] // Empty array - values loaded on-demand when attribute selected
                        }));

                        dispatchMapping({
                            type: 'LOAD_DATA_SUCCESS_PAGINATED',
                            payload: {
                                tab: 'attribute',
                                data: attributesForDropdown,
                                pagination: {
                                    current_page: 1,
                                    total_items: attributesForDropdown.length,
                                    page_size: attributesForDropdown.length,
                                    total_pages: 1,
                                    has_next: false,
                                    has_previous: false
                                }
                            }
                        });
                    }
                }

                // Now load categories
                const params: CategoryRequestParams = {
                    page: 1,
                    page_size,
                    sort_by: sortState.categories.sortBy,
                    sort_order: sortState.categories.sortOrder,
                    ...filterParams
                };

                const res = await getCategoryMappings(channelKey, params);

                if (res && res.done) {
                    if (res.data && res.data.items && res.data.pagination) {
                        // Paginated response
                        dispatchMapping({
                            type: 'LOAD_DATA_SUCCESS_PAGINATED',
                            payload: {
                                tab: 'category',
                                data: res.data.items,
                                pagination: res.data.pagination
                            }
                        });
                        // Only update if not already loaded to prevent infinite loops
                        setLoadedTabs(prev => {
                            if (prev.has('category')) return prev;
                            const newSet = new Set(prev);
                            newSet.add('category');
                            return newSet;
                        });
                    }

                    // Removed: Preloading of attribute values during category table load
                    // Attribute values will be loaded on-demand when user opens attribute dropdown in edit mode
                    // This eliminates 30-40 unnecessary API calls on every category table load
                    // The lazy-loading is handled by loadAttributeValuesForDropdown function
                }
            }
            // NOTE: attributevalue tab loading is handled by the dedicated useEffect
            // that watches selectedAttribute changes (line 637-757). This prevents
            // double API calls when clicking different attributes.
        } catch (error) {
            console.error('Failed to load mapping data:', error);
            toast.error('Failed to load data. Please try again.');
        } finally {
            dispatchMapping({ type: 'SET_LOADING', payload: false });
            isLoadingInitialDataRef.current = false;
            hasCompletedInitialLoadRef.current = true;

            // Run the queued call through the ref so it uses the latest
            // closure (current tab, sort and filters), not this one's.
            const pending = pendingInitialLoadRef.current;
            if (pending) {
                pendingInitialLoadRef.current = null;
                loadInitialDataRef.current(pending.tabOverride);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channelKey, activeTab, sortState]); // Removed buildFilterParams to prevent loops
    loadInitialDataRef.current = loadInitialData;

    // Load more data for infinite scroll
    const loadMoreData = useCallback(async () => {
        if (!channelKey) return;

        const page_size = PAGE_SIZE;

        // Access current pagination state directly from reducer
        const currentState = mappingStateRef.current;
        let currentPagination;
        let nextPage;

        // Get current pagination state for active tab
        if (activeTab === 'attribute') {
            currentPagination = currentState.pagination.attributes;
            nextPage = currentPagination.currentPage + 1;

            // Don't load if already loading or no more data
            if (currentPagination.loading || !currentPagination.hasMore) {
                return;
            }

            // Set loading state
            dispatchMapping({
                type: 'UPDATE_PAGINATION',
                payload: {
                    tab: 'attribute',
                    pagination: { loading: true }
                }
            });

            try {
                const filterParams = buildFilterParamsRef.current('attribute');
                const params: AttributeRequestParams = {
                    page: nextPage,
                    page_size,
                    sort_by: sortState.attributes.sortBy,
                    sort_order: sortState.attributes.sortOrder,
                    ...filterParams
                };

                const res = await getAttributeMappings(channelKey, params);

                if (res && res.done && res.data && res.data.items && res.data.pagination) {
                    // Append new data
                    dispatchMapping({
                        type: 'APPEND_DATA',
                        payload: {
                            tab: 'attribute',
                            data: res.data.items
                        }
                    });

                    // Update pagination state
                    dispatchMapping({
                        type: 'UPDATE_PAGINATION',
                        payload: {
                            tab: 'attribute',
                            pagination: {
                                currentPage: res.data.pagination.current_page,
                                hasMore: res.data.pagination.has_next,
                                loading: false,
                                totalItems: res.data.pagination.total_items
                            }
                        }
                    });
                }
            } catch (error) {
                console.error('Failed to load more attributes:', error);
                toast.error('Failed to load more data.');
                dispatchMapping({
                    type: 'UPDATE_PAGINATION',
                    payload: {
                        tab: 'attribute',
                        pagination: { loading: false }
                    }
                });
            }
        } else if (activeTab === 'attributevalue' && selectedAttribute) {
            currentPagination = currentState.pagination.attributeValues;
            nextPage = currentPagination.currentPage + 1;

            if (currentPagination.loading || !currentPagination.hasMore) {
                return;
            }

            dispatchMapping({
                type: 'UPDATE_PAGINATION',
                payload: {
                    tab: 'attributevalue',
                    pagination: { loading: true }
                }
            });

            try {
                const filterParams = buildFilterParamsRef.current('attributevalue');
                const params: AttributeValueRequestParams = {
                    page: nextPage,
                    page_size,
                    sort_by: sortState.attributeValues.sortBy,
                    sort_order: sortState.attributeValues.sortOrder,
                    ...filterParams
                };

                // Mirror the keying decision in the main fetch effect: the
                // values endpoint expects the *mapped* GMS attribute name,
                // not the incoming one (see the comment near `attributeName`
                // in the main effect for full context).
                const attrNameForApi = selectedAttribute.mapped_attribute
                    || (selectedAttribute.mapped_attributes && selectedAttribute.mapped_attributes[0])
                    || selectedAttribute.attribute;
                const res = await getAttributeValueMappings(channelKey, attrNameForApi, params);

                if (res && res.done && res.data && res.data.items && res.data.pagination) {
                    // Append to flattenedAttributeValues
                    dispatchMapping({
                        type: 'APPEND_DATA',
                        payload: {
                            tab: 'attributevalue',
                            data: res.data.items
                        }
                    });

                    // Also append to the parent attribute's attributeValues
                    const currentAttribute = mappingState.working.attributes.find(
                        attr => attr.attribute === selectedAttribute.attribute
                    );
                    if (currentAttribute) {
                        const newAttributeValues = res.data.items.map((attrVal: any, index: number) => ({
                            ...attrVal,
                            attribute: selectedAttribute.attribute,
                            id: attrVal.attribute_value || `${selectedAttribute.attribute}_${index}_${Date.now()}`
                        }));

                        const updatedAttributeValues = [
                            ...(currentAttribute.attributeValues || []),
                            ...newAttributeValues
                        ];

                        dispatchMapping({
                            type: 'UPDATE_ATTRIBUTE',
                            payload: {
                                id: selectedAttribute.attribute,
                                changes: { attributeValues: updatedAttributeValues }
                            }
                        });
                    }

                    dispatchMapping({
                        type: 'UPDATE_PAGINATION',
                        payload: {
                            tab: 'attributevalue',
                            pagination: {
                                currentPage: res.data.pagination.current_page,
                                hasMore: res.data.pagination.has_next,
                                loading: false,
                                totalItems: res.data.pagination.total_items
                            }
                        }
                    });
                }
            } catch (error) {
                console.error('Failed to load more attribute values:', error);
                toast.error('Failed to load more data.');
                dispatchMapping({
                    type: 'UPDATE_PAGINATION',
                    payload: {
                        tab: 'attributevalue',
                        pagination: { loading: false }
                    }
                });
            }
        } else if (activeTab === 'category') {
            currentPagination = currentState.pagination.categories;
            nextPage = currentPagination.currentPage + 1;

            if (currentPagination.loading || !currentPagination.hasMore) {
                return;
            }

            dispatchMapping({
                type: 'UPDATE_PAGINATION',
                payload: {
                    tab: 'category',
                    pagination: { loading: true }
                }
            });

            try {
                const filterParams = buildFilterParamsRef.current('category');
                const params: CategoryRequestParams = {
                    page: nextPage,
                    page_size,
                    sort_by: sortState.categories.sortBy,
                    sort_order: sortState.categories.sortOrder,
                    ...filterParams
                };

                const res = await getCategoryMappings(channelKey, params);

                if (res && res.done && res.data && res.data.items && res.data.pagination) {
                    const newCategories = res.data.items;

                    dispatchMapping({
                        type: 'APPEND_DATA',
                        payload: {
                            tab: 'category',
                            data: newCategories
                        }
                    });

                    dispatchMapping({
                        type: 'UPDATE_PAGINATION',
                        payload: {
                            tab: 'category',
                            pagination: {
                                currentPage: res.data.pagination.current_page,
                                hasMore: res.data.pagination.has_next,
                                loading: false,
                                totalItems: res.data.pagination.total_items
                            }
                        }
                    });

                    // Removed: Preloading of attribute values for new categories during infinite scroll
                    // Attribute values will be loaded on-demand when user opens attribute dropdown in edit mode
                    // This eliminates unnecessary API calls during scroll operations
                    // The lazy-loading is handled by loadAttributeValuesForDropdown function
                }
            } catch (error) {
                console.error('Failed to load more categories:', error);
                toast.error('Failed to load more data.');
                dispatchMapping({
                    type: 'UPDATE_PAGINATION',
                    payload: {
                        tab: 'category',
                        pagination: { loading: false }
                    }
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channelKey, activeTab, selectedAttribute, sortState]); // buildFilterParams called inline to prevent loops

    // Load more attributes specifically (for the attribute selector list)
    const loadMoreAttributes = useCallback(async () => {
        if (!channelKey) return;

        const page_size = PAGE_SIZE;

        // Access current pagination state from ref for stable callback
        const currentState = mappingStateRef.current;
        const currentPagination = currentState.pagination.attributes;
        const nextPage = currentPagination.currentPage + 1;

        // Don't load if already loading or no more data
        if (currentPagination.loading || !currentPagination.hasMore) {
            return;
        }

        // Set loading state
        dispatchMapping({
            type: 'UPDATE_PAGINATION',
            payload: {
                tab: 'attribute',
                pagination: { loading: true }
            }
        });

        try {
            const isAttributeValueTab = activeTab === 'attributevalue';

            // On the attributevalue tab the sidebar sends exactly what its page-1
            // load sent (Consider/Review only) — never the table search or the
            // attribute tab's own filters.
            const filterParams = isAttributeValueTab
                ? buildSidebarFilterParams()
                : buildFilterParamsRef.current('attribute');

            const params: AttributeRequestParams = {
                page: nextPage,
                page_size,
                sort_by: sortState.attributes.sortBy,
                sort_order: sortState.attributes.sortOrder,
                ...filterParams
            };


            // On the attributevalue tab, always use attributevalue-attributes
            // so paginated loads stay consistent with the deduplicated mapped
            // attribute view used on initial load (see loadInitialData).
            const res = isAttributeValueTab
                ? await getAttributeValueFilteredAttributes(channelKey, params)
                : await getAttributeMappings(channelKey, params);

            if (res && res.done && res.data && res.data.items && res.data.pagination) {
                // Append new data
                dispatchMapping({
                    type: 'APPEND_DATA',
                    payload: {
                        tab: 'attribute',
                        data: res.data.items
                    }
                });

                // Update pagination state
                dispatchMapping({
                    type: 'UPDATE_PAGINATION',
                    payload: {
                        tab: 'attribute',
                        pagination: {
                            currentPage: res.data.pagination.current_page,
                            hasMore: res.data.pagination.has_next,
                            loading: false,
                            totalItems: res.data.pagination.total_items
                        }
                    }
                });
            }
        } catch (error) {
            console.error('Failed to load more attributes:', error);
            toast.error('Failed to load more attributes.');
            dispatchMapping({
                type: 'UPDATE_PAGINATION',
                payload: {
                    tab: 'attribute',
                    pagination: { loading: false }
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channelKey, sortState, activeTab]); // buildFilterParams called inline to prevent loops

    // Server-side search for the attribute selector sidebar
    const searchAttributesList = useCallback(async (searchTerm: string) => {
        if (!channelKey) return;

        // Keep the context ref in sync so context-level refetches (filter
        // changes, etc.) pick up the latest sidebar search term.
        attributeListSearchTermRef.current = searchTerm;

        const page_size = PAGE_SIZE;

        // Set loading state
        dispatchMapping({
            type: 'UPDATE_PAGINATION',
            payload: {
                tab: 'attribute',
                pagination: { loading: true }
            }
        });

        try {
            // Build filter params from the active Consider/Review filters so
            // the sidebar respects BOTH search AND filters when both are
            // applied. Strip table-search (filterParams.search is the table
            // search, not the sidebar search) — that one is table-only.
            const filterParams = buildFilterParamsRef.current('attribute');
            delete filterParams.search;

            const params: AttributeRequestParams = {
                page: 1,
                page_size,
                sort_by: sortState.attributes.sortBy,
                sort_order: sortState.attributes.sortOrder,
                ...filterParams,
            };

            // Only add search param if there's a search term
            if (searchTerm.trim()) {
                params.search = searchTerm.trim();
            }

            // On the attributevalue tab, always use the filtered-by-value API
            // so the sidebar shows deduplicated mapped attribute names (the
            // Phase 2 view). Other tabs continue to use the attribute_mapping
            // endpoint that surfaces incoming names.
            const isAttributeValueTab = activeTab === 'attributevalue';
            const res = isAttributeValueTab
                ? await getAttributeValueFilteredAttributes(channelKey, params)
                : await getAttributeMappings(channelKey, params);

            if (res && res.done && res.data && res.data.items && res.data.pagination) {
                // Replace attribute list with search results
                dispatchMapping({
                    type: 'LOAD_DATA_SUCCESS_PAGINATED',
                    payload: {
                        tab: 'attribute',
                        data: res.data.items,
                        pagination: res.data.pagination
                    }
                });

                // If the currently-selected attribute is no longer in the
                // filtered sidebar results, the right side would keep showing
                // that stale attribute's values. Re-pick based on the new list
                // so the right side stays in sync with what the sidebar shows.
                const currentSelected = selectedAttributeRef.current;
                const stillVisible = currentSelected && res.data.items.some(
                    (a: any) => a.attribute === currentSelected.attribute,
                );
                if (!stillVisible) {
                    const nextSelection = pickAttributeToSelect(
                        res.data.items as MappingAttribute[],
                        null,
                    );
                    // Switching attributes here (the previously-selected one fell out
                    // of the filtered sidebar) must reset the right-side value search,
                    // exactly like an explicit attribute click does (see
                    // AttributeSelectorList.handleAttributeClick). Otherwise the stale
                    // value-search term carries into the newly selected attribute. The
                    // two setState calls batch into one render, so the values table
                    // reloads once for the new attribute with an empty search. (bug 12791)
                    setTableSearchValue('');
                    setSelectedAttribute(nextSelection);
                }
            }
        } catch (error) {
            console.error('[searchAttributesList] Failed:', error);
        } finally {
            dispatchMapping({
                type: 'UPDATE_PAGINATION',
                payload: {
                    tab: 'attribute',
                    pagination: { loading: false }
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channelKey, sortState.attributes, activeTab]); // buildFilterParams called inline; filterStateRef used for current filters

    // Keep refs synchronized with current values
    useEffect(() => {
        mappingStateRef.current = mappingState;
    }, [mappingState]);

    useEffect(() => {
        tableSearchValueRef.current = tableSearchValue;
    }, [tableSearchValue]);

    // Debounce tableSearchValue → debouncedTableSearchValue for the
    // attributevalue tab's value fetch (see the state declaration). Clearing
    // to '' is applied immediately (no 500ms wait) so attribute switches and
    // search-clears reload at once; non-empty terms wait 500ms, mirroring the
    // attribute/category search debounce.
    useEffect(() => {
        if (tableSearchValue === '') {
            setDebouncedTableSearchValue('');
            return;
        }
        const timer = setTimeout(() => {
            setDebouncedTableSearchValue(tableSearchValue);
        }, 500);
        return () => clearTimeout(timer);
    }, [tableSearchValue]);

    useEffect(() => {
        filterStateRef.current = filterState;
    }, [filterState]);

    useEffect(() => {
        selectedAttributeRef.current = selectedAttribute;
    }, [selectedAttribute]);

    // Load data on mount and when channelKey becomes available
    useEffect(() => {
        // Only load if channelKey is available
        if (!channelKey) {
            return;
        }

        loadInitialData();

        // Mark initial mount as complete to allow filter/sort effects to run
        const timer = setTimeout(() => {
            initialMountRef.current = false;
        }, 100);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channelKey]); // Runs when channelKey becomes available

    // Track the last loaded attribute to prevent duplicate API calls
    const lastLoadedAttributeRef = useRef<string | null>(null);
    // Cache key for the SEPARATE unique-mapped-count fetch. Keyed on
    // (attribute, filters, search) but NOT sort — the count is sort-independent,
    // so toggling sort must not re-trigger the ~3s count.
    const lastUniqueCountKeyRef = useRef<string | null>(null);
    const isLoadingAttributeValuesRef = useRef<boolean>(false);
    const lastLoadTimeRef = useRef<number>(0);
    const MIN_LOAD_DISPLAY_TIME = 300; // Minimum 300ms to prevent accidental rapid clicks

    // Reset and reload attribute values when selectedAttribute changes
    useEffect(() => {
        if (activeTab === 'attributevalue') {
            // If no attribute selected, clear the cache and reset data
            if (!selectedAttribute) {
                lastLoadedAttributeRef.current = null;
                dispatchMapping({
                    type: 'RESET_TAB_DATA',
                    payload: { tab: 'attributevalue' }
                });
                return;
            }

            // No skip guard on isReviewed/isConsidered here. The list endpoint
            // is `attributevalue-attributes`, which already filters to mapped
            // attributes whose parent attribute_mapping is reviewed AND
            // considered (see Entities/data_mapping.py:reviewed_targets gate).
            // The `isReviewed` field on each returned row is the AV-row
            // *rollup* (false ⇔ at least one value still pending review), so
            // false here is exactly the state where the user wants to see and
            // review the values — not skip them.

            // FE state (cache key, dispatches, item `attribute` field) is keyed
            // on the *incoming* name — that's what the left list shows and what
            // selectedAttribute / mappingState.attributes use as their id.
            const attributeName = selectedAttribute.attribute;
            // The values endpoint, however, is keyed on the *mapped* GMS
            // attribute name (Phase 2: AV docs live under mapped_attribute).
            // For brand→brand (identity mapping) both are the same, so the
            // mismatch is invisible; for "Video 1 Name" → "Video Name" it
            // returns 0 items. Derive a separate value to send to the API.
            const mappedAttrForApi = selectedAttribute.mapped_attribute
                || (selectedAttribute.mapped_attributes && selectedAttribute.mapped_attributes[0])
                || selectedAttribute.attribute;

            // Build a cache key that includes filters/search/sort to determine if reload is needed
            const currentSort = sortState.attributeValues;
            const cacheKey = JSON.stringify({
                attribute: attributeName,
                filters: filterState,
                search: debouncedTableSearchValue,
                sort: currentSort
            });

            // Fetch the "Unique Mapped Attribute Values" count on its OWN
            // endpoint, in parallel with the data page. On a large attribute the
            // count is a ~3s distinct unwind+group; bundling it made the first
            // page block on a header badge the table doesn't need. Keyed on
            // (attribute, filters, search) but NOT sort — the count is
            // sort-independent — so a sort toggle doesn't re-trigger it.
            // Fire-and-forget: the data load below renders without awaiting this.
            if (channelKey) {
                const countKey = JSON.stringify({
                    attribute: mappedAttrForApi,
                    filters: filterState,
                    search: debouncedTableSearchValue
                });
                if (lastUniqueCountKeyRef.current !== countKey) {
                    lastUniqueCountKeyRef.current = countKey;
                    const countParams: any = buildFilterParams('attributevalue');
                    const dbgSearch = debouncedTableSearchValue.trim();
                    if (dbgSearch) { countParams.search = dbgSearch; } else { delete countParams.search; }
                    dispatchMapping({ type: 'SET_AV_UNIQUE_COUNT', payload: { loading: true } });
                    getAttributeValueUniqueCount(channelKey, mappedAttrForApi, countParams)
                        .then((countRes: any) => {
                            if (countRes && countRes.done && countRes.data) {
                                dispatchMapping({
                                    type: 'SET_AV_UNIQUE_COUNT',
                                    payload: { count: countRes.data.unique_mapped_count, loading: false }
                                });
                            } else {
                                dispatchMapping({ type: 'SET_AV_UNIQUE_COUNT', payload: { loading: false } });
                            }
                        })
                        .catch(() => {
                            // Allow a retry on the next trigger for this key.
                            lastUniqueCountKeyRef.current = null;
                            dispatchMapping({ type: 'SET_AV_UNIQUE_COUNT', payload: { loading: false } });
                        });
                }
            }

            // Prevent duplicate API calls for the same attribute with same filters/search/sort
            if (lastLoadedAttributeRef.current === cacheKey) {
                return;
            }

            // Prevent concurrent API calls - SYNCHRONOUS check with ref
            if (isLoadingAttributeValuesRef.current) {
                return;
            }


            // Mark as loading SYNCHRONOUSLY - but DON'T set cache key yet
            isLoadingAttributeValuesRef.current = true;
            setIsLoadingAttributeValues(true);
            lastLoadTimeRef.current = Date.now();

            // NOTE: DO NOT reset data here - the reducer's LOAD_DATA_SUCCESS_PAGINATED
            // action will preserve unsaved changes from other attributes while loading
            // the new attribute's data

            // Load attribute values immediately
            const loadAttributeValues = async () => {
                if (!channelKey) {
                    isLoadingAttributeValuesRef.current = false;
                    setIsLoadingAttributeValues(false);
                    return;
                }

                try {
                    const page_size = PAGE_SIZE;
                    const filterParams = buildFilterParams('attributevalue');
                    // buildFilterParams reads the raw per-keystroke tableSearchValue;
                    // override with the debounced term so the values fetch fires once
                    // (mirroring the attribute tab) instead of per keystroke.
                    const debouncedSearch = debouncedTableSearchValue.trim();
                    if (debouncedSearch) {
                        filterParams.search = debouncedSearch;
                    } else {
                        delete filterParams.search;
                    }
                    const params: AttributeValueRequestParams = {
                        page: 1,
                        page_size,
                        sort_by: sortState.attributeValues.sortBy,
                        sort_order: sortState.attributeValues.sortOrder,
                        ...filterParams
                    };

                    const res = await getAttributeValueMappings(channelKey, mappedAttrForApi, params);

                    if (res && res.done) {
                        if (res.data && res.data.items && res.data.pagination) {
                            // Validate data integrity before dispatching
                            const items = res.data.items;
                            const pagination = res.data.pagination;

                            // Check 1: Remove duplicates based on attribute_value
                            const uniqueItems = Array.from(
                                new Map(items.map((item: any) => [item.attribute_value, item])).values()
                            );

                            if (uniqueItems.length !== items.length) {
                                console.warn(
                                    `[AttributeValues] Duplicates detected and removed! Original: ${items.length}, Unique: ${uniqueItems.length}`,
                                    'Attribute:', attributeName
                                );
                            }

                            // Check 2: Verify count matches pagination (for first page)
                            if (pagination.current_page === 1) {
                                const expectedCount = Math.min(PAGE_SIZE, pagination.total_items);
                                if (uniqueItems.length !== expectedCount && uniqueItems.length !== pagination.total_items) {
                                    console.warn(
                                        `[AttributeValues] Count mismatch! Received: ${uniqueItems.length}, Expected: ${expectedCount}, Total: ${pagination.total_items}`,
                                        'Attribute:', attributeName
                                    );
                                }
                            }

                            // Transform unique items - ensure attribute field is set
                            const attributeValues = uniqueItems.map((attrVal: any, index: number) => ({
                                ...attrVal,
                                attribute: attributeName,  // Ensure parent attribute is set
                                id: attrVal.attribute_value || `${attributeName}_${index}_${Date.now()}`
                            }));

                            // Dispatch the validated, deduplicated data to flattened structure
                            dispatchMapping({
                                type: 'LOAD_DATA_SUCCESS_PAGINATED',
                                payload: {
                                    tab: 'attributevalue',
                                    data: attributeValues,  // Use transformed data with attribute field
                                    pagination: res.data.pagination
                                }
                            });

                            // Update the parent attribute with the loaded values
                            dispatchMapping({
                                type: 'UPDATE_ATTRIBUTE',
                                payload: {
                                    id: attributeName,
                                    changes: { attributeValues }
                                }
                            });

                            // IMPORTANT: Only set cache key AFTER successful load
                            // This prevents marking incomplete loads as cached
                            lastLoadedAttributeRef.current = cacheKey;
                        }
                    } else if (res && !res.done) {
                        console.error('API call failed:', res);
                        toast.error('Failed to load attribute values.');
                        // Note: We DON'T set cache key on failure, allowing retry
                    }
                } catch (error) {
                    console.error('Failed to load attribute values:', error);
                    toast.error('Failed to load attribute values.');
                    // Note: We DON'T set cache key on error, allowing retry
                } finally {
                    // Ensure minimum display time to prevent accidental rapid clicks
                    const elapsedTime = Date.now() - lastLoadTimeRef.current;
                    const remainingTime = Math.max(0, MIN_LOAD_DISPLAY_TIME - elapsedTime);

                    if (remainingTime > 0) {
                        // Wait for remaining time before clearing loading state
                        setTimeout(() => {
                            isLoadingAttributeValuesRef.current = false;
                            setIsLoadingAttributeValues(false);
                        }, remainingTime);
                    } else {
                        // Clear immediately if minimum time has already passed
                        isLoadingAttributeValuesRef.current = false;
                        setIsLoadingAttributeValues(false);
                    }
                }
            };

            loadAttributeValues();
        }

        // Cleanup function
        return () => {
            // Reset loading state on cleanup
            if (isLoadingAttributeValuesRef.current) {
                isLoadingAttributeValuesRef.current = false;
                setIsLoadingAttributeValues(false);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAttribute, activeTab, channelKey, filterState, debouncedTableSearchValue, sortState.attributeValues]); // debouncedTableSearchValue (not raw tableSearchValue) so value search fires once; buildFilterParams called inline to prevent loops

    // Action handlers
    const updateAttribute = useCallback((id: string, changes: Partial<MappingAttribute>) => {
        dispatchMapping({ type: 'UPDATE_ATTRIBUTE', payload: { id, changes } });
    }, []);

    const updateAttributeValue = useCallback((id: string, changes: Partial<MappingAttributeValue>) => {
        // Phase 2: AV row identity is the composite (mapped_attribute,
        // attribute_value) pair — the same value string exists under multiple
        // mapped attributes. Scope the update to the currently selected mapped
        // attribute so it can't touch another attribute's row. Read from the
        // ref (kept in sync with selectedAttribute) so this callback stays
        // stable.
        const selected = selectedAttributeRef.current as MappingAttribute | null;
        const mappedAttribute = selected?.attribute || (selected as any)?.mapped_attribute || undefined;
        dispatchMapping({ type: 'UPDATE_ATTRIBUTE_VALUE', payload: { id, changes, mappedAttribute } });
    }, []);

    /**
     * Get cached category dropdown attributes or load first page if not cached
     * Returns items and function to load more pages
     */
    const getCategoryDropdownAttributes = useCallback(async (page: number = 1, search: string = ''): Promise<{ items: { value: string; label: string }[]; hasMore: boolean }> => {
        // Only use cache for page 1 with no search
        if (page === 1 && search === '' && categoryDropdownCache.attributes) {
            return {
                items: categoryDropdownCache.attributes.items,
                hasMore: categoryDropdownCache.attributes.hasMore
            };
        }

        // If already loading the same request, return the existing promise
        if (page === 1 && search === '' && attributesLoadingPromiseRef.current) {
            return attributesLoadingPromiseRef.current;
        }

        // Start loading
        if (!channelKey) return { items: [], hasMore: false };

        const loadingPromise = (async () => {
            try {
                const res = await getCategoryDropdownOptions(channelKey, undefined, page, 40, search);
                if (res && res.done && res.data.attributes) {
                    const options = res.data.attributes.map((attr: any) => ({
                        value: attr.mapped_attribute || attr.attribute,
                        label: attr.mapped_attribute || attr.attribute
                    }));
                    const hasMore = res.data.has_more || false;

                    // Only cache page 1 with no search
                    if (page === 1 && search === '') {
                        setCategoryDropdownCache(prev => ({
                            ...prev,
                            attributes: {
                                items: options,
                                hasMore,
                                currentPage: 1
                            }
                        }));
                    }
                    return { items: options, hasMore };
                }
                return { items: [], hasMore: false };
            } finally {
                // Clear the loading promise ref only for cached requests
                if (page === 1 && search === '') {
                    attributesLoadingPromiseRef.current = null;
                }
            }
        })();

        // Store the promise so other calls can reuse it (only for cached requests)
        if (page === 1 && search === '') {
            attributesLoadingPromiseRef.current = loadingPromise;
        }
        return loadingPromise;
    }, [channelKey, categoryDropdownCache.attributes]);

    /**
     * Load more category dropdown attributes (for infinite scroll)
     */
    const loadMoreCategoryDropdownAttributes = useCallback(async (): Promise<{ items: { value: string; label: string }[]; hasMore: boolean }> => {
        if (!channelKey || !categoryDropdownCache.attributes) {
            return { items: [], hasMore: false };
        }

        const nextPage = categoryDropdownCache.attributes.currentPage + 1;

        try {
            const res = await getCategoryDropdownOptions(channelKey, undefined, nextPage, 40, '');
            if (res && res.done && res.data.attributes) {
                const newOptions = res.data.attributes.map((attr: any) => ({
                    value: attr.mapped_attribute || attr.attribute,
                    label: attr.mapped_attribute || attr.attribute
                }));
                const hasMore = res.data.has_more || false;

                const allItems = [...categoryDropdownCache.attributes.items, ...newOptions];

                setCategoryDropdownCache(prev => ({
                    ...prev,
                    attributes: {
                        items: allItems,
                        hasMore,
                        currentPage: nextPage
                    }
                }));

                return { items: allItems, hasMore };
            }
            return { items: categoryDropdownCache.attributes.items, hasMore: false };
        } catch (error) {
            console.error('Failed to load more attributes:', error);
            return { items: categoryDropdownCache.attributes.items, hasMore: false };
        }
    }, [channelKey, categoryDropdownCache.attributes]);

    /**
     * Get cached attribute values for a specific attribute or load first page if not cached
     */
    const getCategoryDropdownAttributeValues = useCallback(async (attributeName: string, page: number = 1, search: string = ''): Promise<{ items: { value: string; label: string }[]; hasMore: boolean }> => {
        // Only use cache for page 1 with no search
        if (page === 1 && search === '' && categoryDropdownCache.attributeValuesByAttribute[attributeName]) {
            const cached = categoryDropdownCache.attributeValuesByAttribute[attributeName];
            return { items: cached.items, hasMore: cached.hasMore };
        }

        // If already loading this attribute (page 1, no search), return the existing promise
        const cacheKey = `${attributeName}`;
        const existingPromise = attributeValuesLoadingPromisesRef.current[cacheKey];
        if (page === 1 && search === '' && existingPromise) {
            return existingPromise;
        }

        // Start loading
        if (!channelKey) return { items: [], hasMore: false };

        const loadingPromise = (async () => {
            try {
                const res = await getCategoryDropdownOptions(channelKey, attributeName, page, 40, search);
                if (res && res.done && res.data.attribute_values) {
                    const options = res.data.attribute_values.map((av: any) => ({
                        value: av.mapped_attribute_value || av.attribute_value,
                        label: av.mapped_attribute_value || av.attribute_value
                    }));
                    const hasMore = res.data.has_more || false;

                    // Only cache page 1 with no search
                    if (page === 1 && search === '') {
                        setCategoryDropdownCache(prev => ({
                            ...prev,
                            attributeValuesByAttribute: {
                                ...prev.attributeValuesByAttribute,
                                [attributeName]: {
                                    items: options,
                                    hasMore,
                                    currentPage: 1
                                }
                            }
                        }));
                    }

                    return { items: options, hasMore };
                }
                return { items: [], hasMore: false };
            } finally {
                // Clear the loading promise ref for this attribute (only for cached requests)
                if (page === 1 && search === '') {
                    delete attributeValuesLoadingPromisesRef.current[cacheKey];
                }
            }
        })();

        // Store the promise so other calls can reuse it (only for cached requests)
        if (page === 1 && search === '') {
            attributeValuesLoadingPromisesRef.current[cacheKey] = loadingPromise;
        }
        return loadingPromise;
    }, [channelKey, categoryDropdownCache.attributeValuesByAttribute]);

    /**
     * Load more attribute values for a specific attribute (for infinite scroll)
     */
    const loadMoreCategoryDropdownAttributeValues = useCallback(async (attributeName: string): Promise<{ items: { value: string; label: string }[]; hasMore: boolean }> => {
        if (!channelKey || !categoryDropdownCache.attributeValuesByAttribute[attributeName]) {
            return { items: [], hasMore: false };
        }

        const cached = categoryDropdownCache.attributeValuesByAttribute[attributeName];
        const nextPage = cached.currentPage + 1;

        try {
            const res = await getCategoryDropdownOptions(channelKey, attributeName, nextPage, 40, '');
            if (res && res.done && res.data.attribute_values) {
                const newOptions = res.data.attribute_values.map((av: any) => ({
                    value: av.mapped_attribute_value || av.attribute_value,
                    label: av.mapped_attribute_value || av.attribute_value
                }));
                const hasMore = res.data.has_more || false;

                const allItems = [...cached.items, ...newOptions];

                setCategoryDropdownCache(prev => ({
                    ...prev,
                    attributeValuesByAttribute: {
                        ...prev.attributeValuesByAttribute,
                        [attributeName]: {
                            items: allItems,
                            hasMore,
                            currentPage: nextPage
                        }
                    }
                }));

                return { items: allItems, hasMore };
            }
            return { items: cached.items, hasMore: false };
        } catch (error) {
            console.error('Failed to load more attribute values:', error);
            return { items: cached.items, hasMore: false };
        }
    }, [channelKey, categoryDropdownCache.attributeValuesByAttribute]);

    /**
     * Clear the category dropdown cache
     * Call this when switching to category tab or after saving changes in other tabs
     */
    const clearCategoryDropdownCache = useCallback(() => {
        setCategoryDropdownCache({
            attributes: null,
            attributeValuesByAttribute: {}
        });
        // Clear loading promise refs
        attributesLoadingPromiseRef.current = null;
        attributeValuesLoadingPromisesRef.current = {};
    }, []);

    const updateCategory = useCallback((id: string, changes: Partial<MappingCategory>) => {
        dispatchMapping({ type: 'UPDATE_CATEGORY', payload: { id, changes } });
    }, []);

    const addAttributePairToCategory = useCallback((categoryId: string) => {
        dispatchMapping({ type: 'ADD_ATTRIBUTE_PAIR_TO_CATEGORY', payload: { categoryId } });
    }, []);

    const removeAttributePair = useCallback((categoryId: string, pairIndex: number) => {
        dispatchMapping({ type: 'REMOVE_ATTRIBUTE_PAIR', payload: { categoryId, pairId: pairIndex.toString() } });
    }, []);

    const updateAttributePair = useCallback((categoryId: string, pairIndex: number, changes: Partial<AttributeValuePair>) => {
        dispatchMapping({ type: 'UPDATE_ATTRIBUTE_PAIR', payload: { categoryId, pairIndex, changes } });
    }, []);

    const copyCategory = useCallback((category: MappingCategory) => {
        const copiedCategoryId = Utils.generate_guid();
        dispatchMapping({ type: 'COPY_CATEGORY', payload: { category, copiedCategoryId } });
        return copiedCategoryId;
    }, []);

    const deleteCategory = useCallback((categoryId: string) => {
        dispatchMapping({ type: 'DELETE_CATEGORY', payload: { categoryId } });
    }, []);

    // Debounced search effect - triggers reload after user stops typing (500ms delay)
    useEffect(() => {
        // Clear previous debounce timer
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }

        // Don't trigger if tab hasn't been loaded yet
        if (!loadedTabs.has(activeTab)) {
            return;
        }

        // Only trigger search if there's an actual search term OR if initial load has completed
        // This prevents the search effect from firing on mount with empty search
        const shouldTriggerSearch = tableSearchValue.trim() || hasCompletedInitialLoadRef.current;

        if (!shouldTriggerSearch) {
            return;
        }

        // Skip this debounce entirely for the attributevalue tab. The
        // selectedAttribute useEffect already has `tableSearchValue` in its
        // deps and fires synchronously on the same render that
        // setTableSearchValue triggers — it builds a new cacheKey, sees the
        // ref doesn't match, and re-fetches values with the new search.
        // If we *also* run the 500ms debounce below, it dispatches
        // RESET_TAB_DATA (wipes the values the selectedAttribute effect
        // just loaded) and nulls lastLoadedAttributeRef, but nothing
        // re-triggers the selectedAttribute effect — its deps haven't
        // changed since the load — so the right pane gets stuck on
        // "No Data Found" while an attribute is clearly selected. Same
        // root cause as the post-Save regression. Attribute and category
        // tabs still rely on the debounce because they don't have an
        // equivalent per-row useEffect.
        if (activeTab === 'attributevalue') {
            return;
        }

        // Set new debounce timer for search
        searchDebounceRef.current = setTimeout(() => {

            // Reset pagination and reload data
            dispatchMapping({
                type: 'RESET_TAB_DATA',
                payload: { tab: activeTab }
            });

            loadInitialData();
        }, 500); // 500ms debounce

        // Cleanup
        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableSearchValue, activeTab, loadedTabs]);

    // Reload data when sort or filter changes (with debouncing to prevent rapid API calls)
    // Note: attributevalue tab is handled by the dedicated selectedAttribute effect
    // Track previous values to detect actual changes
    const prevFilterStateRef = useRef<FilterState>(filterState);
    const prevSortStateRef = useRef<typeof sortState>(sortState);
    const filterSortDebounceRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        // Clear previous debounce timer
        if (filterSortDebounceRef.current) {
            clearTimeout(filterSortDebounceRef.current);
        }

        // Skip on initial mount to prevent double load
        if (initialMountRef.current) {
            prevFilterStateRef.current = filterState;
            prevSortStateRef.current = sortState;
            return;
        }

        // Don't trigger if tab hasn't been loaded yet
        if (!loadedTabs.has(activeTab)) {
            return;
        }

        // Check if filter or sort actually changed. Detect sort changes
        // PER SLICE — the attributevalue tab has two independently-sorted lists:
        // the left attribute sidebar (sortState.attributes) and the right values
        // panel (sortState.attributeValues). A change to the VALUES sort must NOT
        // reload the sidebar — the sidebar's content depends only on filters and
        // its own sort, never on the value sort. (The values reload on their own
        // via the selectedAttribute effect, which is keyed on
        // sortState.attributeValues.) Previously this effect compared the whole
        // sortState object, so a value-sort toggle re-fetched the attribute list
        // (an extra /attributevalue-attributes call) for no reason.
        const prevSort = prevSortStateRef.current;
        const filterChanged = JSON.stringify(prevFilterStateRef.current) !== JSON.stringify(filterState);
        const attributesSortChanged = JSON.stringify(prevSort.attributes) !== JSON.stringify(sortState.attributes);
        const categoriesSortChanged = JSON.stringify(prevSort.categories) !== JSON.stringify(sortState.categories);

        // Update refs
        prevFilterStateRef.current = filterState;
        prevSortStateRef.current = sortState;

        // The list(s) THIS effect reloads:
        //  - attribute tab     → attribute list (its own sort)
        //  - category tab      → category list (its own sort)
        //  - attributevalue tab→ ONLY the left attribute sidebar. The values
        //    panel reloads via its own effect, so attributeValuesSortChanged is
        //    deliberately NOT a reason to reload here.
        const relevantSortChanged = activeTab === 'category'
            ? categoriesSortChanged
            : attributesSortChanged;
        const shouldReload = filterChanged || relevantSortChanged;

        // Reload data if the filters or this tab's own sort changed
        if (shouldReload) {
            // Sort changes should be immediate (user expects instant response)
            // Filter changes should be debounced (300ms) to handle rapid multi-filter selections
            const debounceTime = relevantSortChanged ? 0 : 300;

            filterSortDebounceRef.current = setTimeout(() => {

                // Reset pagination and reload
                dispatchMapping({
                    type: 'RESET_TAB_DATA',
                    payload: { tab: activeTab }
                });

                if (activeTab !== 'attributevalue') {
                    loadInitialData();
                } else {
                    // For attributevalue tab, reload BOTH:
                    // 1. Reset the cache FIRST to ensure attribute values will reload when attribute is selected
                    // 2. Then reload the attributes list (which will auto-select first attribute)
                    // 3. The auto-selection will trigger the selectedAttribute effect which loads attribute values
                    lastLoadedAttributeRef.current = null;
                    reloadAttributesListForAttributeValueTab();
                }
            }, debounceTime);
        }

        // Cleanup
        return () => {
            if (filterSortDebounceRef.current) {
                clearTimeout(filterSortDebounceRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortState, filterState, activeTab, loadedTabs]); // loadInitialData intentionally omitted to prevent re-runs

    // Detect unsaved changes
    const hasUnsavedChanges = useMemo(() => {
        return mappingState.hasUnsavedChanges;
    }, [mappingState.hasUnsavedChanges]);

    // Unsaved changes modal state
    const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);

    // Browser navigation prevention (beforeunload)
    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (hasUnsavedChanges && isEditMode) {
                event.preventDefault();
                event.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges, isEditMode]);


    const getCurrentTabData = useCallback(() => {
        switch (activeTab) {
            case 'attribute':
                return mappingState.working.attributes;
            case 'attributevalue':
                // Filter by selected attribute to ensure only the current attribute's values are shown.
                // In edit mode, flattenedAttributeValues.working may contain modified values from
                // previously selected attributes (preserved for unsaved changes). Without filtering,
                // switching attributes would show values from multiple attributes.
                if (selectedAttribute) {
                    // Phase 2: AV docs are keyed on `mapped_attribute` (not the
                    // legacy incoming `attribute` field). The selector hands us
                    // a row whose `attribute` IS the mapped attribute name (it
                    // comes from the attributevalue-attributes endpoint that
                    // groups by mapped_attribute).
                    const parentName = selectedAttribute.attribute || (selectedAttribute as any).mapped_attribute;
                    return mappingState.flattenedAttributeValues.working.filter(
                        av => av.mapped_attribute === parentName
                    );
                }
                return mappingState.flattenedAttributeValues.working;
            case 'category':
                return mappingState.working.categories;
            default:
                return [];
        }
    }, [activeTab, mappingState, selectedAttribute]);
    /**
     * Transform attribute object to use camelCase field names for PATCH endpoint
     *
     * @param attr - The attribute to transform
     * @param updateType - The type of update ('attribute' or 'attributevalue')
     */
    const transformAttributeForPatch = (attr: MappingAttribute, updateType: 'attribute' | 'attributevalue'): any => {
        // Phase 2: persisted shape is `mapped_attributes: string[]`. Read the
        // list (with legacy scalar fallback for transitional state) and send
        // it as `mappedAttributes` to the BE. The BE accepts any of
        // mappedAttributes / mapped_attributes / mappedAttribute / mapped_attribute.
        const mappedList: string[] = Array.isArray(attr.mapped_attributes)
            ? attr.mapped_attributes.filter(v => typeof v === 'string' && v.length > 0)
            : (attr.mapped_attribute ? [attr.mapped_attribute] : []);

        const transformed: any = {
            attribute: attr.attribute,
            incomingAttribute: attr.attribute,
            isConsidered: attr.isConsidered,
            isReviewed: attr.isReviewed,
        };

        if (updateType === 'attribute') {
            transformed.mappedAttributes = mappedList;
            // Back-compat scalar for any consumer still reading the singular key.
            transformed.mappedAttribute = mappedList[0] ?? '';
        } else if (updateType === 'attributevalue' && mappedList.length > 0) {
            transformed.mappedAttributes = mappedList;
            transformed.mappedAttribute = mappedList[0];
        }

        // Transform attribute values if present. Phase 2: mapped_attribute_value
        // is list-typed; send as `mappedAttributeValues` (the canonical key the
        // BE's `_read_mapped_attribute_values_from_payload` reads first). The
        // earlier transitional triple-send of `mapped_attribute_value` /
        // `mappedAttributeValues` / `mappedAttributeValue` is removed — the BE
        // only ever consumed the first hit.
        if (attr.attributeValues && attr.attributeValues.length > 0) {
            transformed.attributeValues = attr.attributeValues.map(attrVal => {
                const mavList: string[] = Array.isArray(attrVal.mapped_attribute_value)
                    ? (attrVal.mapped_attribute_value as string[]).filter(v => typeof v === 'string' && v.length > 0)
                    : (typeof attrVal.mapped_attribute_value === 'string' && attrVal.mapped_attribute_value
                        ? [attrVal.mapped_attribute_value as unknown as string]
                        : []);
                return {
                    attribute_value: attrVal.attribute_value,
                    mappedAttributeValues: mavList,
                    isConsidered: attrVal.isConsidered,
                    isReviewed: attrVal.isReviewed,
                };
            });
        }

        return transformed;
    };

    const constructPayload = useCallback((mappingState: MappingState): TDataMappingPayload => {
        // Determine update_type based on active tab
        // Since we enforce one tab editing at a time, this is straightforward
        let updateType: 'attribute' | 'attributevalue' | 'category';

        if (activeTab === 'attribute') {
            updateType = 'attribute';
        } else if (activeTab === 'attributevalue') {
            updateType = 'attributevalue';
        } else if (activeTab === 'category') {
            updateType = 'category';
        } else {
            throw new Error(`Invalid active tab: ${activeTab}`);
        }

        // Build payload with update_type at root level (REQUIRED)
        const payload: TDataMappingPayload = {
            update_type: updateType,
            attributes: [],
            categories: []
        };

        // Build payload based on active tab (only include relevant data)
        if (activeTab === 'attribute') {
            // Attribute-level updates
            payload.attributes = [];

            // Pull each modified attribute from the persistent overlay first,
            // falling back to the current page. The Attribute Mapping list is
            // server-paginated/searched/filtered, so a row edited under one
            // filter may no longer be in `working.attributes` when the user
            // saves under another — a `.find` on working alone would silently
            // drop it (the reported bug: "had to save each time I reviewed an
            // attribute when using filter/search"). `modifiedIds` is a Set and
            // the overlay holds one entry per attribute, so each modified
            // attribute is emitted exactly once with its latest value.
            mappingState.modifiedIds.attributes.forEach(attributeId => {
                const attribute = mappingState.modifiedAttributes[attributeId]
                    || mappingState.working.attributes.find(attr => attr.attribute === attributeId);
                if (attribute) {
                    const transformedAttribute = transformAttributeForPatch(attribute, 'attribute');
                    payload.attributes!.push(transformedAttribute);
                }
            });

        } else if (activeTab === 'attributevalue') {
            // Attribute value updates
            payload.attributes = [];

            // Group modified attribute values by their parent attribute.
            //
            // We iterate `flattenedAttributeValues.working` directly (filtered
            // by `modifiedIds.attributeValues`) rather than iterating
            // `modifiedIds` and calling `.find(...)` per id. The id key is
            // `attribute_value` (a value string like "Red" or "Latte"), and
            // the same value string commonly appears under multiple parent
            // attributes (e.g. "Red" exists under both `Color` and `Base
            // Color`). The previous `.find(...)` returned only the first
            // match across `working` and discarded every other parent's
            // edit — visible to the user as "only the last attribute's
            // attribute values are updating". Filtering the working list
            // instead emits one entry per (mapped_attribute, attribute_value)
            // tuple, so each parent group lands in the PATCH.
            const attributeValueGroups = new Map<string, MappingAttributeValue[]>();

            mappingState.flattenedAttributeValues.working.forEach(av => {
                // Composite (mapped_attribute, attribute_value) key — matches
                // how UPDATE_ATTRIBUTE_VALUE records modifications, so an edit
                // under one attribute can't pull in another attribute's row
                // that shares the same value string.
                if (!mappingState.modifiedIds.attributeValues.has(avRowKey(av.mapped_attribute, av.attribute_value))) return;
                // Phase 2: AV docs are keyed on `mapped_attribute` (the parent
                // grouping field) rather than the legacy incoming `attribute`
                // field.
                const parentAttr = av.mapped_attribute;
                if (!attributeValueGroups.has(parentAttr)) {
                    attributeValueGroups.set(parentAttr, []);
                }
                attributeValueGroups.get(parentAttr)!.push(av);
            });

            // Build payload with attribute values grouped by parent attribute.
            //
            // We do NOT gate inclusion on the parent being present in
            // `mappingState.working.attributes`. The AV-tab sidebar list is
            // paginated / filterable / searchable, and the reducer fully
            // *replaces* `working.attributes` on every
            // `LOAD_DATA_SUCCESS_PAGINATED` for `tab='attribute'` (see
            // `mappingReducer.ts` — `attributes: transformedAttributes`).
            // Modified AV rows themselves are preserved across attribute
            // switches by the AV branch of the same action (the merge that
            // carries `modifiedOtherAttributeValues` forward), so a user
            // editing AVs under many attributes in one Edit session ends
            // up with all the edits in `flattenedAttributeValues.working`
            // and in `modifiedIds.attributeValues`. But their PARENT rows
            // long since fell out of `working.attributes` once the sidebar
            // paged / filtered to something else. A `.find(...)` guard
            // here previously dropped every group whose parent had
            // scrolled out of the visible window — only the AV groups
            // whose parents happened to still be in the current sidebar
            // page made it into the PATCH ("only the last 2 attributes
            // got sent" was the on-the-ground symptom).
            //
            // The AV-tab's BE handler (`update_type='attributevalue'` in
            // `_update_single_attribute`) keys the parent lookup on
            // `attribute_data["attribute"]` — i.e. the mapped attribute
            // name we already have as the group key. It does NOT need the
            // parent's `mapped_attributes` list (that field is owned by
            // the Attribute Mapping tab and is explicitly NOT written by
            // AV-tab saves), and a missing parent doc is handled by the
            // phantom-row guard. So a minimal payload keyed on the mapped
            // attribute name is enough when the parent isn't in
            // `working.attributes`. When it IS available we still prefer
            // it so any in-flight working state on the parent (e.g. a
            // freshly toggled review flag the user touched on the
            // Attribute Mapping tab and didn't save yet) round-trips.
            attributeValueGroups.forEach((values, attributeName) => {
                const parentAttribute = mappingState.working.attributes.find(attr => attr.attribute === attributeName);
                // Do NOT spread the parent's `isConsidered` / `isReviewed`
                // into the AV-tab payload. On the AV tab those flags come
                // from `attributevalue-attributes`, which returns them as
                // the *AV-row rollup* (false ⇔ at least one pending value)
                // — they are NOT the actual parent attribute_mapping's
                // flags. Forwarding them caused the BE's
                // `update_type='attributevalue'` branch to write the
                // rollup back as the parent's review state, e.g. a parent
                // legitimately marked reviewed could flip to unreviewed
                // simply because some value under it was still pending.
                // AV-tab saves should only touch value-level flags +
                // (per the BE truncation fix) leave the parent doc alone
                // when the user only edited AVs.
                const baseAttribute: any = {
                    attribute: attributeName,
                    mapped_attributes: parentAttribute?.mapped_attributes ?? [attributeName],
                };
                const transformedAttribute = transformAttributeForPatch(
                    {
                        ...baseAttribute,
                        attributeValues: values
                    },
                    'attributevalue'
                );
                payload.attributes!.push(transformedAttribute);
            });

        } else if (activeTab === 'category') {
            // Category updates
            payload.categories = [];
            payload.catmaps_to_delete = [];

            // Add only categories with meaningful changes
            mappingState.modifiedIds.categories.forEach(categoryId => {
                // Prefer the persistent overlay so an edit to a row that
                // scrolled/filtered off the current page is still saved
                // (working.categories is replaced on every reload).
                const category = mappingState.modifiedCategories[categoryId]
                    || mappingState.working.categories.find(cat => cat.id === categoryId);
                if (category && hasMeaningfulChanges(category, mappingState)) {
                    // Filter out empty attribute pairs before sending
                    const filteredAttributes = category.attribute.filter(pair =>
                        (pair.attribute && pair.attribute.trim() !== '') ||
                        (pair.attributeValue && pair.attributeValue.trim() !== '')
                    );

                    payload.categories!.push({
                        ...category,
                        attribute: filteredAttributes.length > 0 ? filteredAttributes : category.attribute
                    });
                }
            });

            // Add deleted categories
            if (mappingState.deletedCategoryIds.length > 0) {
                payload.catmaps_to_delete = [...mappingState.deletedCategoryIds];
            }
        }

        return payload;
    }, [activeTab]);

    const saveChanges = useCallback(async (): Promise<boolean> => {
        if (!industryId || !channelKey) return false;

        try {
            setIsSaving(true);
            dispatchMapping({ type: 'SET_SAVING', payload: true });

            const payload = constructPayload(mappingState);
            const response = await updateMappingData(payload, channelKey);

            // Clear attribute value cache BEFORE resetting filters
            // This ensures fresh data is loaded when filters reset triggers useEffect
            if (activeTab === 'attributevalue') {
                lastLoadedAttributeRef.current = null;
                isLoadingAttributeValuesRef.current = false;
            }

            setFilterState(initialFilterState);

            if (response && !response.errorfound && response.done) {
                dispatchMapping({ type: 'SAVE_SUCCESS' });
                setIsEditMode(false);
                appContext?.setEditInProgress(false);
                appContext?.setEditSaveHandler(null);
                appContext?.setEditCancelHandler(null);

                // Clear category dropdown cache when saving from Attribute or AttributeValue tabs
                // This ensures category dropdowns show the latest mapped values
                if (activeTab === 'attribute' || activeTab === 'attributevalue') {
                    clearCategoryDropdownCache();
                }

                // Clear the in-table value search so the refreshed values
                // list isn't hidden behind a stale filter. The left-sidebar
                // attribute search is intentionally preserved — clearing it
                // re-renders the full attribute list, the selection useEffect
                // then snaps to the first row, and the user loses the
                // attribute they were just editing. The natural UX after an
                // AV save is "stay on the attribute I was working on", so we
                // leave attributeListSearchTermRef / listSearchResetSignal
                // alone.
                setTableSearchValue('');
                tableSearchValueRef.current = '';
                // Cancel any in-flight search-debounce timer. The
                // setTableSearchValue('') call above re-arms it for 500ms,
                // and the post-save loadInitialData() / selectedAttribute
                // useEffect below will re-fetch values *now* with the
                // updated search. If we leave the debounce armed it will
                // fire ~500ms later and dispatch RESET_TAB_DATA, wiping the
                // values we just loaded — and the selectedAttribute effect
                // won't re-run because none of its deps changed, leaving
                // the right pane stuck on "No Data Found" while an
                // attribute is clearly selected.
                if (searchDebounceRef.current) {
                    clearTimeout(searchDebounceRef.current);
                    searchDebounceRef.current = null;
                }

                toast.success(response?.message || "Changes saved!");
                await loadInitialData();
                return true;
            } else {
                const errorMessage = response?.message || "Failed to save changes.";

                if (response?.err_ids && response.err_ids.length > 0) {
                    const rowNumbers = response.err_ids
                        .map((id: string) => {
                            const index = categoryTableVisibleData?.findIndex(
                                (cat: MappingCategory) => cat.id === id
                            );
                            return index !== -1 ? index + 1 : null;
                        })
                        .filter((num: number | null) => num !== null)
                        .sort((a: number, b: number) => a - b);

                    if (rowNumbers.length > 0) {
                        const detailedMessage = `${errorMessage}\nInvalid data detected in rows: ${rowNumbers.join(', ')}. Please resolve before proceeding.`;
                        toast.error(detailedMessage);
                    } else {
                        toast.error(errorMessage);
                    }
                } else {
                    toast.error(errorMessage);
                }

                return false;
            }
        } catch (error) {
            console.error('Failed to save mapping data:', error);
            return false;
        } finally {
            setIsSaving(false);
            dispatchMapping({ type: 'SET_SAVING', payload: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [industryId, channelKey, mappingState, loadInitialData, constructPayload, categoryTableVisibleData, activeTab, clearCategoryDropdownCache]);


    const getNoDataMessage = useCallback(() => {
        // No "Attribute not reviewed" / "Attribute not considered" branches
        // here. The left list on this tab is sourced from
        // `attributevalue-attributes`, which already gates on the parent
        // attribute_mapping being reviewed+considered — so any attribute the
        // user can select is, by construction, eligible. The `isReviewed`
        // field on the returned row is the AV-row rollup (false ⇔ at least
        // one pending value), not the parent's review status, so it cannot
        // be used to drive an "attribute not reviewed" message.
        // Body is now a constant, so no deps. Previously listed
        // `activeTab` and `selectedAttribute` from when this function
        // branched on tab and parent review state — both branches were
        // removed (see comment above), so the deps would re-create the
        // function on every selection change for no behavioral reason.
        return {
            title: "No Data Found",
            message: "No mapping data is available."
        };
    }, []);

    const getAllCategories = useCallback(() => {
        return mappingState.working.categories;
    }, [mappingState.working.categories]);

    const bulkReviewAllAttributeValues = useCallback(async () => {
        try {
            const dispatch = await bulkReviewAttributeValues(channelKey);

            // The BE dispatches Review All as a background job and returns a
            // task_id (13997 — the synchronous request hit the API gateway
            // timeout on large channels while the work kept running). Poll
            // job status until it finishes so the button spinner reflects the
            // real run instead of a dead request.
            let result: { done: boolean; message: string; updated_count?: number } | null =
                dispatch ?? null;
            if (dispatch?.done && dispatch.task_id) {
                const jobResult = await pollBulkReviewJob(dispatch.task_id);
                if (!jobResult) {
                    // Polling lost the job (network) — the job itself keeps
                    // running server-side.
                    toast.error(
                        "Lost track of the Review All job — it is still running in the background. Refresh in a few minutes to see the result."
                    );
                    return { done: false, message: "Job status polling failed" };
                }
                result = {
                    done: jobResult.error === false && jobResult.done !== false,
                    message: jobResult.status || "",
                    updated_count: jobResult.data?.updated_count
                };
            }

            if (result?.done) {
                toast.success(result.message || "All attribute values marked as reviewed");

                // After bulk-review, any Review:Pending filter would show an
                // empty list even though the data is fine. Also clear the
                // table search for consistency.
                //
                // Critical: do NOT call loadInitialData() directly here. The
                // filter-change useEffect (deps [filterState, ...]) already
                // reloads the sidebar + re-selects the attribute + triggers
                // the values-loading useEffect. Calling loadInitialData on
                // top of that caused a race where the debounced useEffect
                // fired after our direct load completed and dispatched
                // RESET_TAB_DATA for the attributevalue tab, wiping the
                // values we just loaded. That's what produced the "No Data
                // Found" panel after Review All.
                const hadFilter =
                    filterStateRef.current.consider.length > 0 ||
                    filterStateRef.current.review.length > 0;
                const hadSearch = tableSearchValueRef.current.trim() !== '';

                setFilterState(initialFilterState);
                filterStateRef.current = initialFilterState;
                setTableSearchValue('');
                tableSearchValueRef.current = '';

                if (!hadFilter && !hadSearch) {
                    // State didn't actually change so the filter-change
                    // useEffect won't fire. Trigger the reload directly so
                    // reviewed counts refresh everywhere.
                    lastLoadedAttributeRef.current = null;
                    await reloadAttributesListForAttributeValueTab();
                }
                // else: filter-change useEffect picks it up (debounced ~300ms)
                //       and does the sidebar reload + selectedAttribute resync
                //       without any race with a direct loadInitialData call.
            } else {
                toast.error(result?.message || "Failed to bulk review attribute values");
            }
            return result || { done: false, message: "No response from server" };
        } catch (error) {
            console.error("bulkReviewAllAttributeValues ~ error:", error);
            toast.error("Failed to bulk review attribute values");
            return { done: false, message: "An error occurred" };
        }
    }, [channelKey, reloadAttributesListForAttributeValueTab]);

    const discardChanges = useCallback(() => {
        dispatchMapping({ type: 'DISCARD_CHANGES' });
        setIsEditMode(false);
        appContext?.setEditInProgress(false);
        appContext?.setEditSaveHandler(null);
        appContext?.setEditCancelHandler(null);
    }, [appContext]);

    // Tab change with unsaved changes check
    const [pendingTab, setPendingTab] = useState<MappingTabType | null>(null);

    const onTabChange = useCallback((tab: MappingTabType) => {
        // Prevent switching to the same tab
        if (tab === activeTab) {
            return;
        }

        // Show unsaved changes modal if in edit mode or has unsaved changes
        if (isEditMode || hasUnsavedChanges) {
            setPendingTab(tab);
            setShowUnsavedChangesModal(true);
            return;
        }


        // Reset any pending loads when switching tabs
        if (isLoadingInitialDataRef.current) {
            isLoadingInitialDataRef.current = false;
        }
        pendingInitialLoadRef.current = null;

        // Always reset tab data when switching tabs to prevent duplicates
        // This ensures fresh data is loaded each time
        dispatchMapping({
            type: 'RESET_TAB_DATA',
            payload: { tab }
        });

        // Also clear the attribute-list slot (working.attributes + its
        // pagination) on every switch. The sidebar selector and the category
        // dropdowns both render straight from working.attributes, and
        // RESET_TAB_DATA above only clears the *new* tab's own slot. Without
        // this, switching to attributevalue/category leaves the previous tab's
        // (possibly searched/filtered) attribute list on screen until
        // loadInitialData returns — a visible stale flash. Skip when the new
        // tab IS 'attribute' since the dispatch above already cleared it.
        if (tab !== 'attribute') {
            dispatchMapping({
                type: 'RESET_TAB_DATA',
                payload: { tab: 'attribute' }
            });
        }

        // Reset refs when switching away from attributevalue tab
        if (activeTab === 'attributevalue') {
            lastLoadedAttributeRef.current = null;
            isLoadingAttributeValuesRef.current = false;
            setIsLoadingAttributeValues(false);
        }

        // Reset the initial load flag so search effect doesn't trigger on tab switch
        hasCompletedInitialLoadRef.current = false;

        // Clear category dropdown cache when switching TO category tab
        // This ensures fresh data is loaded from the API
        if (tab === 'category') {
            clearCategoryDropdownCache();
        }

        // Switch tab and clear state
        setActiveTab(tab);
        setTableSearchValue('');
        tableSearchValueRef.current = ''; // Sync ref immediately so loadInitialData reads empty search
        attributeListSearchTermRef.current = ''; // Reset sidebar search too so refetches don't use a stale term
        // Clear the carried-over attribute selection (and its ref) so the
        // selectedAttribute effect doesn't fire a throwaway values fetch for
        // the previous tab's selection — loadInitialData re-picks the first
        // attribute from the freshly-loaded list instead.
        setSelectedAttribute(null);
        selectedAttributeRef.current = null;
        // Clear table filters too (Consider / Review / dropdown filters) so
        // the new tab loads fully instead of inheriting the previous tab's
        // filter set. Sync the ref alongside for the same reason as search —
        // loadInitialData reads filterStateRef.current synchronously.
        setFilterState(initialFilterState);
        filterStateRef.current = initialFilterState;
        setAttributeRowActions(null);
        setAttributeValueRowActions(null);
        setCategoryRowActions(null);

        // Mark the new tab as needing to be loaded
        setLoadedTabs((prev: Set<MappingTabType>) => {
            const newSet = new Set(prev);
            newSet.delete(tab); // Remove so it gets reloaded fresh
            return newSet;
        });

        // Load data for the new tab (pass tab explicitly to avoid closure issues)
        loadInitialData(tab);
    }, [hasUnsavedChanges, isEditMode, activeTab, loadInitialData, clearCategoryDropdownCache]);

    // Preload attributes cache when switching to category tab in edit mode
    useEffect(() => {
        if (activeTab === 'category' && isEditMode) {
            getCategoryDropdownAttributes();
        }
    }, [activeTab, isEditMode, getCategoryDropdownAttributes]);

    const onSearchTableData = useCallback((value: string) => {
        setTableSearchValue(value);
    }, []);

    // Refs to avoid stale closures when registering handlers in AppContext
    const saveChangesRef = useRef(saveChanges);
    saveChangesRef.current = saveChanges;
    const discardChangesRef = useRef(discardChanges);
    discardChangesRef.current = discardChanges;

    const handleEditClick = useCallback(async () => {
        // For category tab, ensure attribute names are loaded for dropdown
        // but DON'T preload attribute values - those will be loaded on-demand
        if (activeTab === 'category') {
            const currentAttributes = mappingStateRef.current.working.attributes;
            const hasAttributes = currentAttributes && currentAttributes.length > 0;

            if (!hasAttributes) {

                // Load only attribute names for dropdown (lightweight API call)
                const dropdownRes = await getCategoryDropdownOptions(channelKey);

                if (dropdownRes && dropdownRes.done && dropdownRes.data.attributes) {
                    const attributesForDropdown = dropdownRes.data.attributes.map((attr: any) => ({
                        attribute: attr.attribute,
                        mapped_attribute: attr.mapped_attribute,
                        isConsidered: true,
                        isReviewed: true,
                        attributeValues: [] // Empty - values loaded on-demand when dropdown opened
                    }));

                    dispatchMapping({
                        type: 'LOAD_DATA_SUCCESS_PAGINATED',
                        payload: {
                            tab: 'attribute',
                            data: attributesForDropdown,
                            pagination: {
                                current_page: 1,
                                total_items: attributesForDropdown.length,
                                page_size: attributesForDropdown.length,
                                total_pages: 1,
                                has_next: false,
                                has_previous: false
                            }
                        }
                    });
                }
            }

            // Removed: Preloading of attribute values for all visible categories
            // Attribute values will now be loaded on-demand when user opens a dropdown
            // This reduces unnecessary API calls from 30-40 to 0 on edit click
        }

        // Enter edit mode immediately
        setIsEditMode(true);
        appContext?.setEditInProgress(true);

        // Register save/cancel handlers in AppContext for sidebar/workspace modal
        appContext?.setEditSaveHandler(() => () => saveChangesRef.current());
        appContext?.setEditCancelHandler(() => () => discardChangesRef.current());
    }, [activeTab, channelKey, dispatchMapping, appContext]);

    // Modal handlers (defined after saveChanges, discardChanges, and pendingTab)
    const handleModalSave = useCallback(async () => {
        try {
            await saveChanges();
            // After successful save, proceed with pending action
            if (pendingTab) {
                // Use the onTabChange logic but bypass the unsaved check
                const tab = pendingTab;


                // Reset tab data
                dispatchMapping({
                    type: 'RESET_TAB_DATA',
                    payload: { tab }
                });

                // Reset refs if switching away from attributevalue
                if (activeTab === 'attributevalue') {
                    lastLoadedAttributeRef.current = null;
                    isLoadingAttributeValuesRef.current = false;
                    setIsLoadingAttributeValues(false);
                }

                hasCompletedInitialLoadRef.current = false;

                setActiveTab(tab);
                setTableSearchValue('');
                // Sync the ref immediately — loadInitialData below reads
                // tableSearchValueRef.current, not the React state.
                tableSearchValueRef.current = '';
                attributeListSearchTermRef.current = '';
                // Also clear filter state (Consider / Review / dropdowns) so
                // the new tab doesn't inherit the previous tab's filters.
                setFilterState(initialFilterState);
                filterStateRef.current = initialFilterState;
                setAttributeRowActions(null);
                setAttributeValueRowActions(null);
                setCategoryRowActions(null);
                setPendingTab(null);

                loadInitialData(tab);
            }
            setShowUnsavedChangesModal(false);
        } catch (error) {
            // Save failed, keep modal open
            console.error('Save failed:', error);
        }
    }, [saveChanges, pendingTab, activeTab, loadInitialData]);

    const handleModalDiscard = useCallback(() => {
        discardChanges();
        setShowUnsavedChangesModal(false);

        // Proceed with pending action after discarding
        if (pendingTab) {
            const tab = pendingTab;


            // Reset tab data
            dispatchMapping({
                type: 'RESET_TAB_DATA',
                payload: { tab }
            });

            // Reset refs if switching away from attributevalue
            if (activeTab === 'attributevalue') {
                lastLoadedAttributeRef.current = null;
                isLoadingAttributeValuesRef.current = false;
                setIsLoadingAttributeValues(false);
            }

            hasCompletedInitialLoadRef.current = false;

            setActiveTab(tab);
            setTableSearchValue('');
            // Sync the ref immediately — setState is async but loadInitialData
            // below reads tableSearchValueRef.current, so without this the old
            // search from the previous tab leaks into the new tab's fetch.
            tableSearchValueRef.current = '';
            attributeListSearchTermRef.current = '';
            // Also clear filters so the new tab doesn't inherit the previous
            // tab's Consider / Review / dropdown filter set.
            setFilterState(initialFilterState);
            filterStateRef.current = initialFilterState;
            setAttributeRowActions(null);
            setAttributeValueRowActions(null);
            setCategoryRowActions(null);
            setPendingTab(null);

            loadInitialData(tab);
        }
    }, [discardChanges, pendingTab, activeTab, loadInitialData]);

    const handleModalCancel = useCallback(() => {
        setShowUnsavedChangesModal(false);
        setPendingTab(null);
    }, []);

    // Filter methods
    const updateFilter = useCallback((filterType: string, value: any) => {
        setFilterState((prev: FilterState) => {
            const newState: FilterState = {
                attributes: { ...prev.attributes },
                attributeValues: { ...prev.attributeValues },
                categories: { ...prev.categories },
                consider: [...prev.consider],
                review: [...prev.review]
            };

            // Handle nested filter updates
            if (filterType.includes('.')) {
                const [parentKey, childKey] = filterType.split('.');

                if (parentKey === 'attributes') {
                    newState.attributes = {
                        ...newState.attributes,
                        [childKey]: Array.isArray(value) ? [...value] : value
                    };
                } else if (parentKey === 'attributeValues') {
                    newState.attributeValues = {
                        ...newState.attributeValues,
                        [childKey]: Array.isArray(value) ? [...value] : value
                    };
                } else if (parentKey === 'categories') {
                    newState.categories = {
                        ...newState.categories,
                        [childKey]: Array.isArray(value) ? [...value] : value
                    };
                }
            } else {
                // Handle top-level filters
                if (filterType === 'consider') {
                    newState.consider = Array.isArray(value) ? [...value] : [value];
                } else if (filterType === 'review') {
                    newState.review = Array.isArray(value) ? [...value] : [value];
                }
            }

            return newState;
        });
    }, []);

    const toggleFilterValue = useCallback((filterType: string, value: string) => {
        setFilterState((prev: FilterState) => {
            const newState: FilterState = {
                attributes: { ...prev.attributes },
                attributeValues: { ...prev.attributeValues },
                categories: { ...prev.categories },
                consider: [...prev.consider],
                review: [...prev.review]
            };

            if (filterType.includes('.')) {
                const [parentKey, childKey] = filterType.split('.');

                if (parentKey === 'attributes') {
                    const currentArray = prev.attributes[childKey as keyof typeof prev.attributes] || [];
                    newState.attributes = {
                        ...newState.attributes,
                        [childKey]: currentArray.includes(value)
                            ? currentArray.filter((v: string) => v !== value)
                            : [...currentArray, value]
                    };
                } else if (parentKey === 'attributeValues') {
                    const currentArray = prev.attributeValues[childKey as keyof typeof prev.attributeValues] || [];
                    newState.attributeValues = {
                        ...newState.attributeValues,
                        [childKey]: currentArray.includes(value)
                            ? currentArray.filter((v: string) => v !== value)
                            : [...currentArray, value]
                    };
                } else if (parentKey === 'categories') {
                    const currentArray = prev.categories[childKey as keyof typeof prev.categories] || [];
                    newState.categories = {
                        ...newState.categories,
                        [childKey]: currentArray.includes(value)
                            ? currentArray.filter((v: string) => v !== value)
                            : [...currentArray, value]
                    };
                }
            } else {
                // Handle top-level filters
                if (filterType === 'consider') {
                    const currentArray = prev.consider;
                    newState.consider = currentArray.includes(value as 'on' | 'off')
                        ? currentArray.filter((v: string) => v !== value)
                        : [...currentArray, value as 'on' | 'off'];
                } else if (filterType === 'review') {
                    const currentArray = prev.review;
                    newState.review = currentArray.includes(value as 'reviewed' | 'pending')
                        ? currentArray.filter((v: string) => v !== value)
                        : [...currentArray, value as 'reviewed' | 'pending'];
                }
            }

            return newState;
        });
    }, []);

    const getActiveFilterChips = useCallback((tabType?: MappingTabType): FilterChip[] => {
        const currentTab = tabType || activeTab;
        const chips: FilterChip[] = [];

        // Always show shared filters (Consider/Review)
        filterState.consider.forEach((value: 'on' | 'off') => {
            chips.push({
                id: `consider-${value}`,
                label: value === 'on' ? 'Active' : 'Inactive',
                type: 'shared',
                filterKey: 'consider',
                value: value
            });
        });

        filterState.review.forEach((value: 'reviewed' | 'pending') => {
            chips.push({
                id: `review-${value}`,
                label: value === 'reviewed' ? 'Reviewed' : 'Review Pending',
                type: 'shared',
                filterKey: 'review',
                value: value
            });
        });

        // Tab-specific filters based on currentTab
        if (currentTab === 'attribute') {
            // Incoming Attributes
            filterState.attributes.incoming.forEach((value: string) => {
                chips.push({
                    id: `attributes-incoming-${value}`,
                    label: value,
                    type: 'tab-specific',
                    tabType: 'attribute',
                    filterKey: 'attributes.incoming',
                    value: value,
                    category: 'Incoming Attributes'
                });
            });

            // Mapped Attributes
            filterState.attributes.mapped.forEach((value: string) => {
                chips.push({
                    id: `attributes-mapped-${value}`,
                    label: value,
                    type: 'tab-specific',
                    tabType: 'attribute',
                    filterKey: 'attributes.mapped',
                    value: value,
                    category: 'Mapped Attributes'
                });
            });
        }

        else if (currentTab === 'attributevalue') {
            // Incoming Attribute Values
            filterState.attributeValues.incoming.forEach((value: string) => {
                chips.push({
                    id: `attributeValues-incoming-${value}`,
                    label: value,
                    type: 'tab-specific',
                    tabType: 'attributevalue',
                    filterKey: 'attributeValues.incoming',
                    value: value,
                    category: 'Incoming Attributes Values'
                });
            });

            // Mapped Attribute Values
            filterState.attributeValues.mapped.forEach((value: string) => {
                chips.push({
                    id: `attributeValues-mapped-${value}`,
                    label: value,
                    type: 'tab-specific',
                    tabType: 'attributevalue',
                    filterKey: 'attributeValues.mapped',
                    value: value,
                    category: 'Mapped Attributes Values'
                });
            });
        }

        else if (currentTab === 'category') {
            // Reporting Categories
            filterState.categories.reportingCategory.forEach((value: string) => {
                chips.push({
                    id: `categories-reportingCategory-${value}`,
                    label: value,
                    type: 'tab-specific',
                    tabType: 'category',
                    filterKey: 'categories.reportingCategory',
                    value: value,
                    category: 'Reporting Categories'
                });
            });

            // Mapped Categories
            filterState.categories.mappedCategory.forEach((value: string) => {
                chips.push({
                    id: `categories-mappedCategory-${value}`,
                    label: value,
                    type: 'tab-specific',
                    tabType: 'category',
                    filterKey: 'categories.mappedCategory',
                    value: value,
                    category: 'Mapped Categories'
                });
            });
        }

        return chips;
    }, [activeTab, filterState]);

    const clearFilter = useCallback((filterId: string) => {
        // Find the filter chip by ID to get its filterKey and value
        const allFilters = getActiveFilterChips();
        const filterToRemove = allFilters.find((chip: FilterChip) => chip.id === filterId);

        if (filterToRemove) {
            toggleFilterValue(filterToRemove.filterKey, filterToRemove.value);
        }
    }, [toggleFilterValue, getActiveFilterChips]);

    const clearAllFilters = useCallback(() => {
        setFilterState(initialFilterState);
    }, []);

    return (
        <MappingContext.Provider value={{
            industryId,
            channelKey,
            readPermission,
            updatePermission,
            bulkUpdatePermission,
            activeTab,
            loading: mappingState.loading,
            isLoadingAttributeValues,
            onTabChange,
            isEditMode,
            tableSearchValue,
            onSearchTableData,
            isFilterOpen,
            setIsFilterOpen,
            handleEditClick,
            isSaving,
            setIsSaving,
            mappingState,
            updateAttribute,
            updateAttributeValue,
            updateCategory,
            addAttributePairToCategory,
            removeAttributePair,
            updateAttributePair,
            getCurrentTabData,
            getCategoryDropdownAttributes,
            loadMoreCategoryDropdownAttributes,
            getCategoryDropdownAttributeValues,
            loadMoreCategoryDropdownAttributeValues,
            clearCategoryDropdownCache,
            saveChanges,
            discardChanges,
            loadInitialData,
            // Pagination
            pagination: mappingState.pagination,
            loadMoreData,
            loadMoreAttributes,
            // Row actions
            attributeRowActions,
            attributeValueRowActions,
            categoryRowActions,
            setAttributeRowActions,
            setAttributeValueRowActions,
            setCategoryRowActions,
            // Filter properties
            filterState,
            updateFilter,
            toggleFilterValue,
            clearFilter,
            clearAllFilters,
            getActiveFilterChips,
            copyCategory,
            deleteCategory,
            selectedAttribute,
            setSelectedAttribute,
            getNoDataMessage,
            setCategoryTableVisibleData,
            getAllCategories,
            bulkReviewAllAttributeValues,
            searchAttributesList,
            listSearchResetSignal,
            // Sort/filter state and handlers
            sortState,
            handleSortChange
        }}>
            {children}
            {showUnsavedChangesModal && (
                <UnsavedChangesModal
                    id="mapping-context-unsaved-changes-modal"
                    title="Unsaved Changes"
                    message="You have unsaved changes. Do you want to save them before proceeding?"
                    confirmText="Save Changes"
                    cancelText="Cancel"
                    discardText="Discard changes"
                    onClose={handleModalCancel}
                    onConfirm={handleModalSave}
                    onDiscard={handleModalDiscard}
                    saveInProgress={isSaving}
                />
            )}
        </MappingContext.Provider>
    );
};